import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { GlassPanel } from "../components/GlassPanel";
import { FormModal, Field, SelectField } from "./LeadsPage";
import {
  Users, Shield, UserPlus, KeyRound, Pencil, Trash2, Mail, Settings as SettingsIcon, ChevronRight, MessageSquare, Clock, Bell, Sparkles, Building2, Sun, Moon, User as UserIcon, X, Wrench, Phone, Archive, Palette, Timer, FileText, RotateCcw, Eye, Wand2, BrainCircuit, Star, Download, AlertCircle,
} from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useTheme } from "../context/ThemeContext";
import { useTenant } from "../context/TenantContext";
import { useBusinessProfile } from "../hooks/useBusinessProfile";
import { useUiPref } from "../hooks/useUiPref";
import QuoteHistoryPanel from "../components/QuoteHistoryPanel";
import SkillBuilderPanel from "../components/SkillBuilderPanel";
import AdvancedQuotingSection from "../components/AdvancedQuotingSection";
import GoogleReviewSection from "../components/GoogleReviewSection";
import { refreshCustomAreas } from "../hooks/useCustomAreas";

const SECTIONS = [
  { key: "business", label: "Business Profile", icon: Building2, description: "Your shop name and details shown to customers", group: "operations" },
  { key: "branding", label: "Branding", icon: Palette, description: "Logo + accent colour applied across customer emails, public pages, and your signed-in app", group: "operations" },
  { key: "quote_template", label: "Quote Template", icon: FileText, description: "Customise every color, label, and section on the customer-facing quote PDF", group: "operations" },
  { key: "quoting", label: "AI Quoting", icon: Sparkles, description: "Pricing history & AI quoting reference", group: "operations" },
  { key: "accounts", label: "Accounts", icon: Users, description: "Create and manage staff accounts and roles", group: "operations" },
  { key: "roles", label: "Roles & Permissions", icon: Shield, description: "Toggle who can see prices, edit jobs, manage inventory, and more", group: "operations" },
  { key: "workday_hours", label: "Working Hours", icon: Timer, description: "Per-day hours the workshop is booked for — drives calendar workload bars", group: "operations" },
  { key: "auto_archive", label: "Auto-archive", icon: Archive, description: "How long leads and quotes stay before auto-archiving", group: "operations" },
  { key: "services", label: "Services offered", icon: Wrench, description: "Tick the job types + installations your shop actually offers", group: "operations" },
  { key: "custom_areas", label: "Custom Areas", icon: Sparkles, description: "Per-vehicle custom application areas so unique jobs stop landing in 'Custom'", group: "operations" },
  { key: "installation_terms", label: "Installation Terms", icon: FileText, description: "Customer-facing T&Cs. Sent when a booking is confirmed and required before a job can enter the workshop.", group: "operations" },
  { key: "job_workflow", label: "Job workflow", icon: Wrench, description: "Toggles for the way jobs move through the shop", group: "operations" },
  { key: "password", label: "My Password", icon: KeyRound, description: "Change your own sign-in password", group: "operations" },
  { key: "appearance", label: "Appearance", icon: Sun, description: "Light or dark theme for this device", group: "operations" },
  { key: "sms", label: "SMS Reminders", icon: MessageSquare, description: "Auto-text customers before their booking", badge: "Coming soon", group: "operations" },
  { key: "email", label: "Email", icon: Mail, description: "Use your own domain (Resend) so quotes, invoices and invites arrive from your shop", group: "operations" },
  { key: "google_review", label: "Google Reviews", icon: Star, description: "Auto-send a review link when jobs are collected", group: "operations" },
  // Advanced / AI tools — visually separated in the nav so they don't get
  // mixed up with day-to-day operational settings.
  { key: "advanced_quoting", label: "Advanced Quoting", icon: BrainCircuit, description: "Feed the AI quoting agent your job history and per-area pricing memory", group: "ai" },
  { key: "skill_builder", label: "Skill Builder", icon: Wand2, description: "Interview-driven builder for your shop's bespoke quoting prompt", group: "ai" },
];

const ROLE_OPTIONS = ["admin", "office", "installer", "assistant"];

/**
 * Custom hook — pulls the live roles list (built-ins + admin-created custom
 * roles) from the RBAC API so the account create/edit dropdowns reflect
 * what's actually assignable. Falls back to the hardcoded four if the API
 * isn't reachable (e.g. older backend).
 */
function useAssignableRoles() {
  const [roles, setRoles] = useState(
    ROLE_OPTIONS.map((r) => ({ value: r, label: r }))
  );
  useEffect(() => {
    let alive = true;
    api.get("/roles")
      .then(({ data }) => {
        if (!alive) return;
        setRoles(data.map((r) => ({
          value: r.role,
          label: `${r.display_name || r.role}${r.is_custom ? " (custom)" : ""}`,
        })));
      })
      .catch(() => { /* keep fallback */ });
    return () => { alive = false; };
  }, []);
  return roles;
}

// Personal accent palette for installers/assistants — colour-codes calendar
// bars, timeline chips, and future badge spots so admins can eyeball who's
// on which job.
export const INSTALLER_COLORS = [
  { hex: "#3B82F6", name: "Blue" },
  { hex: "#6366F1", name: "Indigo" },
  { hex: "#8B5CF6", name: "Violet" },
  { hex: "#EC4899", name: "Pink" },
  { hex: "#F43F5E", name: "Rose" },
  { hex: "#F59E0B", name: "Amber" },
  { hex: "#10B981", name: "Emerald" },
  { hex: "#14B8A6", name: "Teal" },
  { hex: "#06B6D4", name: "Cyan" },
  { hex: "#F97316", name: "Orange" },
  { hex: "#84CC16", name: "Lime" },
  { hex: "#94A3B8", name: "Slate" },
];

// Job workshop stages (mirrors backend pipeline.SUB_STATUSES["job"]).
// Displayed in the "Profile · qualifications" modal so admins can tick which
// stages a staff member is trained to perform.
const JOB_STAGES = [
  { value: "unbooked", label: "Unbooked" },
  { value: "booked", label: "Booked" },
  { value: "awaiting_design", label: "Awaiting design" },
  { value: "design_in_progress", label: "Design in progress" },
  { value: "design_approved", label: "Design approved" },
  { value: "in_store", label: "In store" },
  { value: "wash_down", label: "Wash down" },
  { value: "clay_bar", label: "Clay bar" },
  { value: "disassembly", label: "Disassembly" },
  { value: "wrapping", label: "Wrapping" },
  { value: "assembly", label: "Assembly" },
  { value: "quality_check", label: "Quality check" },
  { value: "ready_for_collection", label: "Ready for collection" },
  { value: "collected", label: "Collected" },
];

export default function SettingsPage() {
  const [section, setSection] = useState("accounts");
  const { user } = useAuth();
  // Office scoping applies to raw office AND any custom role that
  // inherits from office (base_role='office'). Admin always sees
  // everything, so we OR in that check up top for safety.
  const isAdmin = user?.role === "admin";
  const isOffice = !isAdmin && (user?.role === "office" || user?.base_role === "office");
  // Office role: same settings shell but user-management panel and admin-only
  // auto-archive / working-hours tuners are hidden.
  const visibleSections = SECTIONS.filter((s) => !(isOffice && (s.key === "accounts" || s.key === "quoting" || s.key === "auto_archive" || s.key === "workday_hours" || s.key === "roles" || s.key === "skill_builder" || s.key === "advanced_quoting" || s.key === "email")));

  // If office landed on the default "accounts" section, bounce them to business.
  useEffect(() => {
    if (isOffice && section === "accounts") setSection("business");
  }, [isOffice, section]);

  return (
    <div className="max-w-6xl mx-auto space-y-6">
      <div>
        <div className="text-label flex items-center gap-2"><SettingsIcon className="w-3 h-3" /> {isOffice ? "Office" : "Master · Admin"}</div>
        <h1 className="font-heading text-4xl sm:text-5xl text-white mt-1">Settings</h1>
        <p className="text-sm text-slate-400 mt-2 max-w-xl">
              Manage your team, roles, and workshop preferences.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-[220px_1fr] gap-6">
        {/* Left rail */}
        <GlassPanel className="!p-3 h-fit">
          <nav className="flex md:flex-col gap-1">
            {visibleSections.map((s, idx) => {
              const Icon = s.icon;
              const active = section === s.key;
              // Insert a divider + tiny group heading before the first "ai"
              // section so admin-only power tools sit visually apart from
              // day-to-day operational settings.
              const prev = idx > 0 ? visibleSections[idx - 1] : null;
              const showDivider = s.group === "ai" && (!prev || prev.group !== "ai");
              return (
                <React.Fragment key={s.key}>
                  {showDivider && (
                    <div className="hidden md:block pt-3 mt-2 border-t border-white/5">
                      <div className="text-[9px] uppercase tracking-[0.25em] text-slate-500 px-3 pb-1">
                        Advanced · AI tools
                      </div>
                    </div>
                  )}
                  <button
                    type="button"
                    data-testid={`settings-nav-${s.key}`}
                    onClick={() => setSection(s.key)}
                    className={`flex items-center justify-between gap-2 rounded-2xl px-3 py-2.5 text-sm transition text-left w-full ${
                      active
                        ? "bg-white/[0.08] text-white border border-white/10"
                        : "text-slate-300 hover:bg-white/[0.04]"
                    }`}
                  >
                    <span className="flex items-center gap-2 min-w-0">
                      <Icon className="w-4 h-4 shrink-0" />
                      <span className="truncate">{s.label}</span>
                    </span>
                    {s.badge ? (
                      <span className="pill pill-muted text-[9px] shrink-0">{s.badge}</span>
                    ) : (
                      <ChevronRight className={`w-3.5 h-3.5 transition ${active ? "text-white" : "text-slate-500"}`} />
                    )}
                  </button>
                </React.Fragment>
              );
            })}
          </nav>
        </GlassPanel>

        {/* Right pane */}
        <div className="min-w-0">
          {section === "business" && <BusinessProfileSection />}
          {section === "branding" && !isOffice && <BrandingSection />}
          {section === "quote_template" && <QuoteTemplateSection />}
          {section === "roles" && !isOffice && <RolesSection />}
          {section === "quoting" && !isOffice && <QuoteHistoryPanel />}
          {section === "accounts" && !isOffice && <AccountsSection />}
          {section === "auto_archive" && !isOffice && <AutoArchiveSection />}
          {section === "services" && !isOffice && <EnabledServicesSection />}
          {section === "custom_areas" && !isOffice && <CustomAreasSection />}
          {section === "installation_terms" && !isOffice && <InstallationTermsSection />}
          {section === "job_workflow" && !isOffice && <JobWorkflowSection />}
          {section === "workday_hours" && !isOffice && <WorkdayHoursSection />}
          {section === "password" && <PasswordSection />}
          {section === "appearance" && <AppearanceSection />}
          {section === "sms" && <SmsRemindersSection />}
          {section === "email" && !isOffice && <EmailSection />}
          {section === "google_review" && <GoogleReviewSection />}
          {section === "skill_builder" && !isOffice && <SkillBuilderPanel />}
          {section === "advanced_quoting" && !isOffice && <AdvancedQuotingSection />}
        </div>
      </div>
    </div>
  );
}

/* ---------------------------------------------------------------------------- */
/*                            Business Profile                                 */
/* ---------------------------------------------------------------------------- */
function BusinessProfileSection() {
  const { user } = useAuth();
  const canEdit = user?.role === "admin" || user?.role === "office";
  const { profile, refresh } = useBusinessProfile();
  const [form, setForm] = useState({
    name: "", tagline: "", contact_email: "", contact_phone: "", address: "", abn: "", website: "", logo_url: "",
  });
  const [busy, setBusy] = useState(false);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (loaded) return;
    api.get("/business-profile")
      .then(({ data }) => { setForm({ ...form, ...data }); setLoaded(true); })
      .catch(() => setLoaded(true));
  }, []);

  const save = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return toast.error("Business name is required");
    setBusy(true);
    try {
      await api.put("/business-profile", form);
      await refresh();
      toast.success("Business profile saved. Customer emails and PDFs will use the new name.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <GlassPanel className="!p-6 max-w-2xl" data-testid="settings-business">
      <div className="mb-5">
        <div className="text-label flex items-center gap-2"><Building2 className="w-3 h-3" /> Identity</div>
        <h2 className="font-heading text-2xl text-white mt-1">Business Profile</h2>
        <p className="text-sm text-slate-400 mt-1">
          This is the name customers see on quotes, invoices, emails, PDFs and the embed form.
          WrapConsole stays the internal app name — everything customer-facing switches to <b>{profile?.name || "your business"}</b>.
        </p>
      </div>
      {!canEdit && (
        <div className="text-xs text-amber-200 bg-amber-500/10 border border-amber-400/20 rounded-xl px-3 py-2 mb-4">
          Only admins or office staff can edit the business profile. Current name: <b>{profile?.name}</b>
        </div>
      )}
      <form onSubmit={save} className="space-y-4">
        <div className="grid md:grid-cols-2 gap-3">
          <Field label="Business name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} testId="biz-name" required />
          <Field label="Tagline (optional)" value={form.tagline} onChange={(v) => setForm({ ...form, tagline: v })} testId="biz-tagline" />
        </div>
        <div className="grid md:grid-cols-2 gap-3">
          <Field label="Contact email" value={form.contact_email} onChange={(v) => setForm({ ...form, contact_email: v })} testId="biz-email" />
          <Field label="Contact phone" value={form.contact_phone} onChange={(v) => setForm({ ...form, contact_phone: v })} testId="biz-phone" />
        </div>
        <Field label="Address" value={form.address} onChange={(v) => setForm({ ...form, address: v })} testId="biz-address" />
        <div className="grid md:grid-cols-2 gap-3">
          <Field label="ABN / registration #" value={form.abn} onChange={(v) => setForm({ ...form, abn: v })} testId="biz-abn" />
          <Field label="Website" value={form.website} onChange={(v) => setForm({ ...form, website: v })} testId="biz-website" />
        </div>
        <Field label="Logo URL (optional)" value={form.logo_url} onChange={(v) => setForm({ ...form, logo_url: v })} testId="biz-logo" />
        {canEdit && (
          <div className="flex justify-end pt-1">
            <button
              data-testid="biz-save"
              type="submit"
              disabled={busy}
              className="bg-white text-black rounded-full px-5 py-2.5 text-sm font-semibold hover:bg-slate-100 disabled:opacity-60"
            >
              {busy ? "Saving…" : "Save profile"}
            </button>
          </div>
        )}
      </form>
    </GlassPanel>
  );
}

/* ---------------------------------------------------------------------------- */
/*                            Shop Branding                                    */
/* ---------------------------------------------------------------------------- */
function BrandingSection() {
  const { tenant, refetch } = useTenant();
  const [logoUrl, setLogoUrl] = useState("");
  const [accent, setAccent] = useState("");
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const fileRef = React.useRef(null);

  useEffect(() => {
    if (tenant) {
      setLogoUrl(tenant.logo_url || "");
      setAccent(tenant.primary_color || "");
    }
  }, [tenant?.id]);

  const resolved = resolveLogoUrlFromRelative(logoUrl);

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    if (f.size > 2 * 1024 * 1024) { toast.error("Logo must be 2MB or smaller"); return; }
    const ok = ["image/png", "image/jpeg", "image/webp", "image/svg+xml"].includes(f.type);
    if (!ok) { toast.error("PNG, JPG, WEBP, or SVG only"); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/tenants/me/branding/logo", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setLogoUrl(data.logo_url || "");
      await refetch();
      toast.success("Logo uploaded");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const saveFields = async () => {
    setSaving(true);
    try {
      const payload = {};
      // Only patch primary_color + logo_url if they changed (URL-paste path)
      if ((tenant?.primary_color || "") !== accent) payload.primary_color = accent;
      const trimmedLogo = (logoUrl || "").trim();
      if ((tenant?.logo_url || "") !== trimmedLogo && !trimmedLogo.startsWith("/api/public/")) {
        // Only PATCH when user pasted a URL — uploads already persisted.
        payload.logo_url = trimmedLogo;
      }
      if (Object.keys(payload).length === 0) {
        toast.info("Nothing changed");
        setSaving(false);
        return;
      }
      await api.patch("/tenants/me/branding", payload);
      await refetch();
      toast.success("Branding saved");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const clearLogo = async () => {
    if (!window.confirm("Remove the shop logo?")) return;
    try {
      await api.patch("/tenants/me/branding", { logo_url: "" });
      setLogoUrl("");
      await refetch();
      toast.success("Logo removed");
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const PRESETS = ["#1D4ED8", "#059669", "#DC2626", "#D97706", "#7C3AED", "#DB2777", "#0891B2", "#111827"];

  return (
    <GlassPanel className="!p-6" data-testid="settings-branding">
      <div className="mb-5">
        <div className="text-label flex items-center gap-2"><Palette className="w-3 h-3" /> Shop branding</div>
        <h2 className="font-heading text-2xl text-white mt-1">Branding</h2>
        <p className="text-xs text-slate-400 mt-1 max-w-xl">
          Your shop's logo and accent colour appear on customer emails, public quote &amp;
          booking pages, invite links, and in your signed-in app's sidebar.
        </p>
      </div>

      {/* Logo */}
      <div className="rounded-2xl border border-white/5 bg-black/20 p-5 mb-5">
        <div className="text-label mb-3">Logo</div>
        <div className="flex items-center gap-5">
          <div className="w-20 h-20 rounded-2xl bg-white/5 border border-white/10 flex items-center justify-center overflow-hidden shrink-0">
            {resolved ? (
              <img
                data-testid="branding-logo-preview"
                src={resolved}
                alt="Logo preview"
                className="w-full h-full object-cover"
                onError={(e) => { e.currentTarget.style.display = "none"; }}
              />
            ) : (
              <Sparkles className="w-6 h-6 text-slate-500" />
            )}
          </div>
          <div className="flex-1 space-y-2">
            <div className="flex items-center gap-2">
              <button
                onClick={() => fileRef.current?.click()}
                disabled={uploading}
                data-testid="branding-upload-btn"
                className="flex items-center gap-1.5 bg-white text-black text-xs font-semibold rounded-full px-4 py-2 hover:bg-slate-100 disabled:opacity-60"
              >
                {uploading ? "Uploading…" : "Upload image"}
              </button>
              <input
                ref={fileRef}
                data-testid="branding-upload-input"
                type="file"
                accept="image/png,image/jpeg,image/webp,image/svg+xml"
                className="hidden"
                onChange={onFile}
              />
              {logoUrl && (
                <button
                  onClick={clearLogo}
                  data-testid="branding-clear-btn"
                  className="flex items-center gap-1 text-xs text-slate-400 hover:text-red-300 transition px-3 py-2"
                >
                  <X className="w-3 h-3" /> Remove
                </button>
              )}
            </div>
            <div className="text-[11px] text-slate-500">PNG, JPG, WEBP or SVG. Max 2 MB. Square works best.</div>
            <div>
              <label className="text-[10px] uppercase tracking-widest text-slate-500 block mb-1">
                Or paste a URL
              </label>
              <input
                data-testid="branding-logo-url"
                type="url"
                value={logoUrl.startsWith("/api/public/") ? "" : logoUrl}
                onChange={(e) => setLogoUrl(e.target.value)}
                placeholder="https://your-site.com/logo.png"
                className="w-full bg-black/40 border border-white/10 rounded-xl px-3 py-2 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-white/30"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Accent */}
      <div className="rounded-2xl border border-white/5 bg-black/20 p-5 mb-5">
        <div className="text-label mb-3">Accent colour</div>
        <div className="flex items-center gap-3 flex-wrap">
          <input
            type="color"
            data-testid="branding-accent-picker"
            value={accent || "#1D4ED8"}
            onChange={(e) => setAccent(e.target.value.toUpperCase())}
            className="w-12 h-10 rounded-lg bg-black/40 border border-white/10 cursor-pointer"
          />
          <input
            type="text"
            data-testid="branding-accent-hex"
            value={accent}
            onChange={(e) => setAccent(e.target.value.toUpperCase())}
            placeholder="#1D4ED8"
            className="w-32 bg-black/40 border border-white/10 rounded-xl px-3 py-2 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-white/30 font-mono"
          />
          <div className="flex items-center gap-1.5">
            {PRESETS.map((c) => (
              <button
                key={c}
                data-testid={`branding-preset-${c.replace("#","")}`}
                onClick={() => setAccent(c)}
                title={c}
                className={`w-6 h-6 rounded-md border transition ${accent === c ? "border-white ring-2 ring-white/30" : "border-white/10 hover:border-white/40"}`}
                style={{ background: c }}
              />
            ))}
          </div>
          {accent && (
            <button
              onClick={() => setAccent("")}
              className="text-xs text-slate-400 hover:text-red-300 ml-2"
            >
              Clear
            </button>
          )}
        </div>
        {accent && (
          <div className="mt-4 flex items-center gap-3">
            <div className="text-[10px] uppercase tracking-widest text-slate-500">Preview</div>
            <div className="rounded-full px-4 py-2 text-xs font-semibold" style={{ background: accent, color: "#fff" }}>
              Example CTA
            </div>
          </div>
        )}
      </div>

      <div className="flex items-center justify-end gap-2">
        <button
          onClick={saveFields}
          disabled={saving}
          data-testid="branding-save-btn"
          className="bg-white text-black font-semibold rounded-full px-5 py-2 text-sm hover:bg-slate-100 disabled:opacity-60"
        >
          {saving ? "Saving…" : "Save branding"}
        </button>
      </div>
    </GlassPanel>
  );
}

function resolveLogoUrlFromRelative(url) {
  if (!url) return "";
  if (url.startsWith("http://") || url.startsWith("https://") || url.startsWith("data:")) return url;
  const base = process.env.REACT_APP_BACKEND_URL || "";
  return base.replace(/\/+$/, "") + url;
}

/* ---------------------------------------------------------------------------- */
/*                            Quote PDF Template                               */
/* ---------------------------------------------------------------------------- */
function QuoteTemplateSection() {
  const { user } = useAuth();
  const canEdit = user?.role === "admin" || user?.role === "office";
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/quote-template");
      setForm(data);
    } catch (_) {
      toast.error("Failed to load template settings");
    }
  };
  useEffect(() => { load(); }, []);

  const patch = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  // Live preview — POSTs the current in-progress form to the backend which
  // renders a sample quote PDF with the unsaved values. Returned as a blob
  // URL so it can be embedded in an iframe next to the form.
  const refreshPreview = async (payload = form) => {
    if (!payload) return;
    setPreviewLoading(true);
    try {
      const res = await api.post("/quote-template/preview", payload, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      setPreviewUrl((prev) => { if (prev) URL.revokeObjectURL(prev); return url; });
    } catch (_) {
      toast.error("Failed to render preview");
    } finally {
      setPreviewLoading(false);
    }
  };

  // Debounced auto-preview whenever a field changes — 500ms window so
  // rapid typing doesn't hammer the backend.
  useEffect(() => {
    if (!form) return;
    const h = setTimeout(() => refreshPreview(form), 500);
    return () => clearTimeout(h);
  }, [form]);

  useEffect(() => {
    return () => { if (previewUrl) URL.revokeObjectURL(previewUrl); };
  }, [previewUrl]);

  const save = async () => {
    if (!canEdit) return;
    setBusy(true);
    try {
      const { data } = await api.put("/quote-template", form);
      setForm(data);
      toast.success("Quote template saved");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };

  const reset = async () => {
    if (!canEdit) return;
    if (!window.confirm("Reset the quote template to factory defaults?")) return;
    setBusy(true);
    try {
      const { data } = await api.post("/quote-template/reset");
      setForm(data);
      toast.success("Reset to defaults");
    } catch (err) {
      toast.error("Reset failed");
    } finally { setBusy(false); }
  };

  if (!form) {
    return (
      <GlassPanel className="!p-6" data-testid="settings-quote-template-loading">
        <div className="text-sm text-slate-400 italic">Loading template…</div>
      </GlassPanel>
    );
  }

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[1fr_460px] gap-4" data-testid="settings-quote-template">
      {/* Editor pane */}
      <GlassPanel className="!p-6 space-y-6">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <div className="text-label flex items-center gap-2"><FileText className="w-3 h-3" /> Customer-facing PDF</div>
            <h2 className="font-heading text-2xl text-white mt-1">Quote Template</h2>
            <p className="text-sm text-slate-400 mt-1 max-w-lg">
              Rebrand the quote sheet without touching code. Every colour, label,
              and disclaimer updates the PDF customers download and receive by
              email.
            </p>
          </div>
          <button
            type="button"
            onClick={reset}
            disabled={!canEdit || busy}
            data-testid="quote-template-reset"
            className="inline-flex items-center gap-1.5 text-xs uppercase tracking-widest text-slate-400 hover:text-white disabled:opacity-50"
            title="Restore factory defaults"
          >
            <RotateCcw className="w-3 h-3" /> Reset
          </button>
        </div>

        {/* Section 1 — Header */}
        <FieldGroup title="Header">
          <div className="grid grid-cols-2 gap-3">
            <TextInput
              testId="qt-document-label"
              label="Document label"
              value={form.document_label}
              onChange={(v) => patch("document_label", v)}
              placeholder="QUOTE"
              disabled={!canEdit}
            />
            <ColorInput
              testId="qt-accent-color"
              label="Accent colour"
              value={form.accent_color}
              onChange={(v) => patch("accent_color", v)}
              disabled={!canEdit}
            />
          </div>
          <ToggleRow
            testId="qt-show-id"
            label="Show quote reference number"
            checked={form.show_id}
            onChange={(v) => patch("show_id", v)}
            disabled={!canEdit}
          />
          <ToggleRow
            testId="qt-show-tagline"
            label="Show business tagline"
            checked={form.show_tagline}
            onChange={(v) => patch("show_tagline", v)}
            disabled={!canEdit}
          />
        </FieldGroup>

        {/* Section 2 — Business info visible on the PDF */}
        <FieldGroup title="Business info shown on the PDF">
          <p className="text-[11px] text-slate-500">Tick the details from your Business Profile that should appear under the header.</p>
          <ToggleRow testId="qt-show-address" label="Business address"       checked={form.show_business_address} onChange={(v) => patch("show_business_address", v)} disabled={!canEdit} />
          <ToggleRow testId="qt-show-phone"   label="Business phone number"  checked={form.show_business_phone}   onChange={(v) => patch("show_business_phone", v)}   disabled={!canEdit} />
          <ToggleRow testId="qt-show-email"   label="Business email"         checked={form.show_business_email}   onChange={(v) => patch("show_business_email", v)}   disabled={!canEdit} />
          <ToggleRow testId="qt-show-website" label="Business website"       checked={form.show_business_website} onChange={(v) => patch("show_business_website", v)} disabled={!canEdit} />
          <ToggleRow testId="qt-show-abn"     label="ABN"                    checked={form.show_business_abn}     onChange={(v) => patch("show_business_abn", v)}     disabled={!canEdit} />
          {form.show_business_abn && (
            <TextInput
              testId="qt-abn-prefix"
              label="ABN prefix (leave blank to hide)"
              value={form.abn_prefix}
              onChange={(v) => patch("abn_prefix", v)}
              placeholder="ABN"
              disabled={!canEdit}
            />
          )}
        </FieldGroup>

        {/* Section 3 — Customer block */}
        <FieldGroup title="Customer block">
          <div className="grid grid-cols-2 gap-3">
            <TextInput
              testId="qt-prepared-for"
              label='"Prepared for" label'
              value={form.prepared_for_label}
              onChange={(v) => patch("prepared_for_label", v)}
              disabled={!canEdit}
            />
            <SelectInput
              testId="qt-date-format"
              label="Date format"
              value={form.date_format}
              onChange={(v) => patch("date_format", v)}
              options={[
                { value: "long",  label: "August 18, 2026" },
                { value: "short", label: "18/08/2026" },
                { value: "iso",   label: "2026-08-18" },
              ]}
              disabled={!canEdit}
            />
          </div>
          <ToggleRow
            testId="qt-show-date"
            label="Show date issued"
            checked={form.show_date_issued}
            onChange={(v) => patch("show_date_issued", v)}
            disabled={!canEdit}
          />
          {form.show_date_issued && (
            <TextInput
              testId="qt-date-prefix"
              label='Date prefix (leave blank to hide "Date issued:")'
              value={form.date_issued_prefix}
              onChange={(v) => patch("date_issued_prefix", v)}
              placeholder="Date issued:"
              disabled={!canEdit}
            />
          )}
        </FieldGroup>

        {/* Section 4 — Table styling */}
        <FieldGroup title="Line-item table">
          <div className="grid grid-cols-3 gap-3">
            <TextInput
              testId="qt-col-description"
              label="Column 1 label"
              value={form.col_description_label}
              onChange={(v) => patch("col_description_label", v)}
              disabled={!canEdit}
            />
            <TextInput
              testId="qt-col-vehicle"
              label="Column 2 label"
              value={form.col_vehicle_label}
              onChange={(v) => patch("col_vehicle_label", v)}
              disabled={!canEdit}
            />
            <TextInput
              testId="qt-col-amount"
              label="Column 3 label"
              value={form.col_amount_label}
              onChange={(v) => patch("col_amount_label", v)}
              disabled={!canEdit}
            />
          </div>
          <div className="grid grid-cols-3 gap-3">
            <ColorInput
              testId="qt-table-header-bg"
              label="Header background"
              value={form.table_header_bg}
              onChange={(v) => patch("table_header_bg", v)}
              disabled={!canEdit}
            />
            <ColorInput
              testId="qt-table-header-text"
              label="Header text"
              value={form.table_header_text}
              onChange={(v) => patch("table_header_text", v)}
              disabled={!canEdit}
            />
            <ColorInput
              testId="qt-body-text"
              label="Body text"
              value={form.body_text_color}
              onChange={(v) => patch("body_text_color", v)}
              disabled={!canEdit}
            />
          </div>
          <TextInput
            testId="qt-total-label"
            label='"Total" row label'
            value={form.total_label}
            onChange={(v) => patch("total_label", v)}
            disabled={!canEdit}
          />
        </FieldGroup>

        {/* Section 5 — Notes */}
        <FieldGroup title="Notes section">
          <ToggleRow
            testId="qt-show-notes"
            label="Include notes when the quote has them"
            checked={form.show_notes}
            onChange={(v) => patch("show_notes", v)}
            disabled={!canEdit}
          />
          <TextInput
            testId="qt-notes-heading"
            label="Notes heading"
            value={form.notes_heading}
            onChange={(v) => patch("notes_heading", v)}
            disabled={!canEdit || !form.show_notes}
          />
        </FieldGroup>

        {/* Section 6 — Footer */}
        <FieldGroup title="Footer">
          <NumberInput
            testId="qt-validity"
            label="Quote validity (days)"
            value={form.validity_days}
            onChange={(v) => patch("validity_days", v)}
            min={0} max={365}
            disabled={!canEdit}
          />
          <TextInput
            testId="qt-thank-you"
            label="Thank-you line (optional)"
            value={form.thank_you_text}
            onChange={(v) => patch("thank_you_text", v)}
            placeholder="e.g. Thanks for choosing Vinyl Wrapping Australia!"
            disabled={!canEdit}
          />
          <TextAreaInput
            testId="qt-footer-text"
            label="Footer disclaimer"
            hint="Use {validity_days} to insert the number above automatically."
            value={form.footer_text}
            onChange={(v) => patch("footer_text", v)}
            rows={3}
            disabled={!canEdit}
          />
        </FieldGroup>

        <div className="flex justify-end pt-2">
          <button
            type="button"
            onClick={save}
            disabled={!canEdit || busy}
            data-testid="quote-template-save"
            className="bg-white text-black px-5 py-2.5 rounded-full text-sm font-semibold hover:bg-slate-100 disabled:opacity-50"
          >
            {busy ? "Saving…" : "Save template"}
          </button>
        </div>
      </GlassPanel>

      {/* Preview pane */}
      <GlassPanel className="!p-4 space-y-2 lg:sticky lg:top-4 self-start" data-testid="quote-template-preview-panel">
        <div className="flex items-center justify-between">
          <div className="text-label flex items-center gap-2"><Eye className="w-3 h-3" /> Live preview</div>
          <div className="flex items-center gap-2">
            {previewLoading && <span className="text-[10px] uppercase tracking-widest text-slate-500">Rendering…</span>}
            {previewUrl && (
              <a
                href={previewUrl}
                download="quote-preview.pdf"
                data-testid="quote-template-preview-download"
                className="text-[10px] uppercase tracking-widest text-slate-400 hover:text-white"
              >
                Download
              </a>
            )}
          </div>
        </div>
        <div className="rounded-2xl overflow-hidden border border-white/10 bg-neutral-950 h-[720px]">
          {previewUrl ? (
            <embed
              src={previewUrl}
              type="application/pdf"
              title="Quote preview"
              data-testid="quote-template-preview-iframe"
              className="w-full h-full"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-sm text-slate-500 italic">
              Adjust any field above to see the PDF here.
            </div>
          )}
        </div>
        <p className="text-[10px] text-slate-500 leading-relaxed">
          Preview uses a sample customer, vehicle, and pricing so you can eyeball
          layout without touching real quotes.
        </p>
      </GlassPanel>
    </div>
  );
}

/* Small primitives used by QuoteTemplateSection ------------------------------ */
function FieldGroup({ title, children }) {
  return (
    <section className="space-y-3">
      <div className="text-[10px] uppercase tracking-[0.25em] text-slate-500">{title}</div>
      <div className="space-y-3">{children}</div>
    </section>
  );
}

function TextInput({ label, value, onChange, placeholder, disabled, testId }) {
  return (
    <div>
      <label className="text-label block mb-1.5">{label}</label>
      <input
        data-testid={testId}
        type="text"
        value={value || ""}
        placeholder={placeholder}
        disabled={disabled}
        onChange={(e) => onChange(e.target.value)}
        className="w-full bg-black/40 border border-white/10 rounded-xl px-3 py-2.5 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-white/30 disabled:opacity-50"
      />
    </div>
  );
}

function TextAreaInput({ label, value, onChange, rows = 3, hint, disabled, testId }) {
  return (
    <div>
      <label className="text-label block mb-1.5">{label}</label>
      <textarea
        data-testid={testId}
        value={value || ""}
        rows={rows}
        disabled={disabled}
        onChange={(e) => onChange(e.target.value)}
        className="w-full bg-black/40 border border-white/10 rounded-xl px-3 py-2.5 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-white/30 disabled:opacity-50"
      />
      {hint && <div className="text-[10px] text-slate-500 mt-1">{hint}</div>}
    </div>
  );
}

function NumberInput({ label, value, onChange, min, max, disabled, testId }) {
  return (
    <div>
      <label className="text-label block mb-1.5">{label}</label>
      <input
        data-testid={testId}
        type="number"
        min={min}
        max={max}
        value={value ?? ""}
        disabled={disabled}
        onChange={(e) => onChange(Number(e.target.value) || 0)}
        className="w-full bg-black/40 border border-white/10 rounded-xl px-3 py-2.5 text-sm text-white focus:outline-none focus:border-white/30 disabled:opacity-50"
      />
    </div>
  );
}

function SelectInput({ label, value, onChange, options, disabled, testId }) {
  return (
    <div>
      <label className="text-label block mb-1.5">{label}</label>
      <select
        data-testid={testId}
        value={value}
        disabled={disabled}
        onChange={(e) => onChange(e.target.value)}
        className="w-full bg-black/40 border border-white/10 rounded-xl px-3 py-2.5 text-sm text-white focus:outline-none focus:border-white/30 disabled:opacity-50"
      >
        {options.map((o) => (
          <option key={o.value} value={o.value} className="bg-neutral-900">{o.label}</option>
        ))}
      </select>
    </div>
  );
}

function ColorInput({ label, value, onChange, disabled, testId }) {
  const safe = /^#[0-9A-Fa-f]{6}$/.test(value) ? value : "#0F172A";
  return (
    <div>
      <label className="text-label block mb-1.5">{label}</label>
      <div className="flex items-center gap-2 bg-black/40 border border-white/10 rounded-xl px-2 py-1.5">
        <input
          type="color"
          value={safe}
          disabled={disabled}
          onChange={(e) => onChange(e.target.value.toUpperCase())}
          data-testid={`${testId}-picker`}
          className="w-9 h-9 rounded-md bg-transparent border-0 cursor-pointer disabled:opacity-50"
        />
        <input
          type="text"
          value={value || ""}
          disabled={disabled}
          onChange={(e) => onChange(e.target.value)}
          data-testid={testId}
          placeholder="#0F172A"
          className="flex-1 bg-transparent text-sm text-white placeholder-slate-500 focus:outline-none uppercase disabled:opacity-50"
        />
      </div>
    </div>
  );
}

function ToggleRow({ label, checked, onChange, disabled, testId }) {
  return (
    <label className={`flex items-center justify-between gap-3 py-1.5 ${disabled ? "opacity-50" : "cursor-pointer"}`}>
      <span className="text-sm text-slate-200">{label}</span>
      <input
        type="checkbox"
        data-testid={testId}
        checked={!!checked}
        disabled={disabled}
        onChange={(e) => onChange(e.target.checked)}
        className="w-4 h-4 accent-emerald-400"
      />
    </label>
  );
}




/* ---------------------------------------------------------------------------- */
/*                         Roles & Permissions                                 */
/* ---------------------------------------------------------------------------- */
function RolesSection() {
  const [catalogue, setCatalogue] = useState(null);   // {permissions, builtin_roles}
  const [roles, setRoles] = useState([]);              // full roles list
  const [dirty, setDirty] = useState({});              // {role: {perm: bool}}
  const [busy, setBusy] = useState(false);
  const [creating, setCreating] = useState(false);

  const load = async () => {
    try {
      const [{ data: cat }, { data: rs }] = await Promise.all([
        api.get("/roles/permissions-catalogue"),
        api.get("/roles"),
      ]);
      setCatalogue(cat);
      setRoles(rs);
      setDirty({});
    } catch (_) {
      toast.error("Failed to load roles");
    }
  };
  useEffect(() => { load(); }, []);

  const togglePerm = (role, key, next) => {
    setDirty((d) => ({
      ...d,
      [role]: { ...(d[role] || {}), [key]: next },
    }));
  };

  const effectivePerm = (role, key) => {
    if (dirty[role.role]?.[key] !== undefined) return dirty[role.role][key];
    return !!role.permissions?.[key];
  };

  const saveRole = async (role) => {
    const changes = dirty[role.role];
    if (!changes) return;
    setBusy(true);
    try {
      const finalPerms = { ...role.permissions, ...changes };
      await api.put(`/roles/${role.role}`, { permissions: finalPerms });
      toast.success(`${role.display_name} updated`);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };

  const removeRole = async (role) => {
    if (!window.confirm(`Delete the "${role.display_name}" role? Users assigned to it must be reassigned first.`)) return;
    try {
      await api.delete(`/roles/${role.role}`);
      toast.success("Role deleted");
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Delete failed");
    }
  };

  const resetAll = async () => {
    if (!window.confirm("Reset all built-in roles to factory defaults? Custom roles are untouched.")) return;
    setBusy(true);
    try {
      await api.post("/roles/reset");
      toast.success("Reset to defaults");
      await load();
    } catch (_) {
      toast.error("Reset failed");
    } finally { setBusy(false); }
  };

  if (!catalogue) {
    return (
      <GlassPanel className="!p-6" data-testid="settings-roles-loading">
        <div className="text-sm text-slate-400 italic">Loading roles…</div>
      </GlassPanel>
    );
  }

  // Group permissions for tidy sections in the matrix
  const groups = catalogue.permissions.reduce((acc, p) => {
    (acc[p.group] = acc[p.group] || []).push(p);
    return acc;
  }, {});

  return (
    <div className="space-y-4" data-testid="settings-roles">
      <GlassPanel className="!p-6 space-y-2">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <div className="text-label flex items-center gap-2"><Shield className="w-3 h-3" /> Access control</div>
            <h2 className="font-heading text-2xl text-white mt-1">Roles & Permissions</h2>
            <p className="text-sm text-slate-400 mt-1 max-w-2xl">
              Toggle what each role can see and do. The four built-in roles
              (Admin, Office, Installer, Assistant) can&apos;t be deleted — they&apos;re
              the security floor — but every permission below is yours to
              flip. Need a role like &quot;Manager&quot; or &quot;Detailer&quot;? Create a custom
              role that inherits from a built-in and tune it exactly how you
              want.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              data-testid="roles-new"
              onClick={() => setCreating(true)}
              className="inline-flex items-center gap-1.5 bg-white text-black text-xs font-semibold px-4 py-2 rounded-full hover:bg-slate-100"
            >
              <UserPlus className="w-3.5 h-3.5" /> New role
            </button>
            <button
              type="button"
              data-testid="roles-reset"
              disabled={busy}
              onClick={resetAll}
              className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-slate-400 hover:text-white"
            >
              Reset defaults
            </button>
          </div>
        </div>
      </GlassPanel>

      {roles.map((role) => (
        <GlassPanel
          key={role.role}
          className="!p-5"
          data-testid={`role-card-${role.role}`}
        >
          <div className="flex items-start justify-between gap-3 mb-4 flex-wrap">
            <div>
              <div className="flex items-center gap-2">
                <span className="font-heading text-xl text-white">{role.display_name}</span>
                {role.is_custom && (
                  <span className="pill pill-info text-[9px]">CUSTOM</span>
                )}
                {!role.is_custom && (
                  <span className="pill pill-muted text-[9px]">BUILT-IN</span>
                )}
              </div>
              <div className="text-xs text-slate-500 mt-1">
                Key: <code className="text-slate-400">{role.role}</code>
                {role.base_role && <> · Inherits from <code className="text-slate-400">{role.base_role}</code></>}
              </div>
            </div>
            <div className="flex items-center gap-2">
              {dirty[role.role] && (
                <button
                  type="button"
                  data-testid={`role-save-${role.role}`}
                  disabled={busy}
                  onClick={() => saveRole(role)}
                  className="bg-white text-black text-xs font-semibold px-4 py-1.5 rounded-full hover:bg-slate-100 disabled:opacity-50"
                >
                  Save changes
                </button>
              )}
              {role.is_custom && (
                <button
                  type="button"
                  data-testid={`role-delete-${role.role}`}
                  onClick={() => removeRole(role)}
                  className="p-1.5 rounded-lg bg-white/5 hover:bg-red-500/20 text-slate-400 hover:text-red-300"
                  title="Delete role"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            {Object.entries(groups).map(([groupName, perms]) => (
              <div key={groupName} className="bg-white/[0.03] border border-white/5 rounded-2xl p-3 space-y-1.5">
                <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-1.5">{groupName}</div>
                {perms.map((p) => {
                  const on = effectivePerm(role, p.key);
                  // Admin's manage_roles + manage_staff are locked ON so an
                  // admin can't accidentally lock themselves out. Everything
                  // else is editable.
                  const locked = role.role === "admin" && (p.key === "manage_roles" || p.key === "manage_staff");
                  return (
                    <label
                      key={p.key}
                      className={`flex items-center justify-between gap-3 py-1 ${locked ? "opacity-60" : "cursor-pointer"}`}
                      title={p.hint}
                    >
                      <div className="min-w-0">
                        <div className="text-sm text-slate-200 leading-tight">{p.label}</div>
                        {p.hint && <div className="text-[10px] text-slate-500 leading-tight mt-0.5">{p.hint}</div>}
                      </div>
                      <input
                        type="checkbox"
                        data-testid={`role-${role.role}-perm-${p.key}`}
                        checked={on}
                        disabled={locked}
                        onChange={(e) => togglePerm(role.role, p.key, e.target.checked)}
                        className="w-4 h-4 accent-emerald-400 shrink-0"
                      />
                    </label>
                  );
                })}
              </div>
            ))}
          </div>
        </GlassPanel>
      ))}

      {creating && (
        <CreateRoleModal
          catalogue={catalogue}
          onClose={() => setCreating(false)}
          onSaved={() => { setCreating(false); load(); }}
        />
      )}
    </div>
  );
}

function CreateRoleModal({ catalogue, onClose, onSaved }) {
  const [displayName, setDisplayName] = useState("");
  const [baseRole, setBaseRole] = useState("installer");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!displayName.trim()) return;
    // Slugify display name to role key.
    const key = displayName.trim().toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_|_$/g, "");
    if (!/^[a-z][a-z0-9_]*$/.test(key)) {
      toast.error("Role name must start with a letter and use letters/numbers only");
      return;
    }
    setBusy(true);
    try {
      await api.post("/roles", {
        role: key,
        display_name: displayName.trim(),
        base_role: baseRole,
        permissions: {},   // inherit defaults from base_role
      });
      toast.success(`Created "${displayName}" role`);
      onSaved?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Create failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <form
        onSubmit={submit}
        data-testid="create-role-modal"
        className="w-full max-w-md bg-neutral-900 border border-white/10 rounded-2xl p-6 space-y-4"
      >
        <div className="flex items-center justify-between">
          <h3 className="font-heading text-xl text-white">New role</h3>
          <button type="button" onClick={onClose} className="text-slate-400 hover:text-white">
            <X className="w-4 h-4" />
          </button>
        </div>
        <div>
          <label className="text-label block mb-1.5">Role name</label>
          <input
            data-testid="create-role-name"
            type="text"
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            placeholder="e.g. Detailer, Shop Manager, Lead Wrapper"
            required
            className="w-full bg-black/40 border border-white/10 rounded-xl px-3 py-2.5 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-white/30"
          />
        </div>
        <div>
          <label className="text-label block mb-1.5">Inherits from</label>
          <select
            data-testid="create-role-base"
            value={baseRole}
            onChange={(e) => setBaseRole(e.target.value)}
            className="w-full bg-black/40 border border-white/10 rounded-xl px-3 py-2.5 text-sm text-white focus:outline-none focus:border-white/30"
          >
            {catalogue.builtin_roles.filter((r) => r !== "admin").map((r) => (
              <option key={r} value={r} className="bg-neutral-900">{r}</option>
            ))}
          </select>
          <div className="text-[10px] text-slate-500 mt-1">
            The new role starts with the same defaults as its base role. Tweak individual permissions after creating it.
          </div>
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <button
            type="button"
            onClick={onClose}
            className="text-xs px-4 py-2 rounded-full bg-white/5 border border-white/10 text-slate-300 hover:bg-white/10"
          >
            Cancel
          </button>
          <button
            type="submit"
            data-testid="create-role-submit"
            disabled={busy}
            className="text-xs font-semibold px-5 py-2 rounded-full bg-white text-black hover:bg-slate-100 disabled:opacity-50"
          >
            {busy ? "Creating…" : "Create role"}
          </button>
        </div>
      </form>
    </div>
  );
}


/* ---------------------------------------------------------------------------- */
/*                              Appearance                                     */
/* ---------------------------------------------------------------------------- */
function AppearanceSection() {
  const { theme, setTheme } = useTheme();
  // Pipeline preference — surface here (Appearance) so it lives with the
  // other "how the UI looks on this device" toggles. Backed by localStorage
  // via useUiPref, so it applies to whoever's logged in on this browser.
  const [showAssignees, setShowAssignees] = useUiPref("pipeline.showAssignees", false);
  const opts = [
    { key: "dark", label: "Dark", desc: "Liquid glass on ink — the default WrapConsole aesthetic", Icon: Moon },
    { key: "light", label: "Light", desc: "Soft slate glass for well-lit workshops and daytime desks", Icon: Sun },
  ];
  return (
    <GlassPanel className="!p-6 max-w-2xl" data-testid="settings-appearance">
      <div className="mb-5">
        <div className="text-label flex items-center gap-2"><Sun className="w-3 h-3" /> Theme</div>
        <h2 className="font-heading text-2xl text-white mt-1">Appearance</h2>
        <p className="text-sm text-slate-400 mt-1">
          Choose how WrapConsole renders on this device. Your preference is remembered per browser.
        </p>
      </div>
      <div className="grid md:grid-cols-2 gap-3">
        {opts.map(({ key, label, desc, Icon }) => {
          const active = theme === key;
          return (
            <button
              key={key}
              type="button"
              data-testid={`theme-${key}`}
              onClick={() => { setTheme(key); toast.success(`${label} theme applied`); }}
              className={`group text-left rounded-3xl border p-5 transition ${active
                ? "bg-white text-black border-white font-semibold shadow-lg"
                : "bg-white/[0.03] border-white/10 text-slate-300 hover:bg-white/[0.06]"}`}
            >
              <div className="flex items-center gap-3 mb-2">
                <span className={`w-10 h-10 rounded-2xl border flex items-center justify-center ${active ? "bg-black/10 border-black/10 text-black" : "bg-white/5 border-white/10 text-slate-200"}`}>
                  <Icon className="w-4 h-4" />
                </span>
                <div className="font-heading text-lg">{label}</div>
                {active && <span className="ml-auto text-[10px] uppercase tracking-widest">Active</span>}
              </div>
              <div className={`text-xs leading-relaxed ${active ? "text-black/70" : "text-slate-400"}`}>{desc}</div>
            </button>
          );
        })}
      </div>
      <div className="text-[11px] text-slate-500 mt-4">
        Note: light theme is a functional first pass. A few glass panels and marketing accents are still tuned for the dark aesthetic — expect a polish pass in future releases.
      </div>

      {/* Pipeline density preference. Toggles the small installer chip on
          each stage pill in the Job Detail's "Overall Pipeline" side rail.
          Off by default so casual users get the leanest layout; power users
          who juggle multiple installers can flip it on. */}
      <div className="mt-8 pt-6 border-t border-white/5">
        <div className="text-label flex items-center gap-2"><UserIcon className="w-3 h-3" /> Pipeline density</div>
        <h3 className="font-heading text-lg text-white mt-1">Show assignee on stage pills</h3>
        <p className="text-sm text-slate-400 mt-1 mb-4">
          Adds a small &ldquo;who&apos;s on this stage&rdquo; chip to every stage pill on the Job Detail&apos;s Overall Pipeline. Off keeps the pipeline lean — flip it on when you want to see ownership without opening Staff-per-Stage.
        </p>
        <label
          htmlFor="pref-show-assignees"
          className={`flex items-center gap-3 rounded-2xl border p-4 cursor-pointer transition ${
            showAssignees
              ? "bg-white/[0.06] border-white/20"
              : "bg-white/[0.02] border-white/10 hover:bg-white/[0.04]"
          }`}
        >
          <div className="flex-1">
            <div className="text-sm text-white font-medium">
              Show installer names inline on stage pills
            </div>
            <div className="text-xs text-slate-400 mt-0.5">
              Applies only to the vertical &ldquo;Overall Pipeline&rdquo; panel on the Job Detail page.
            </div>
          </div>
          <input
            id="pref-show-assignees"
            type="checkbox"
            data-testid="pref-pipeline-show-assignees"
            checked={!!showAssignees}
            onChange={(e) => {
              setShowAssignees(e.target.checked);
              toast.success(e.target.checked ? "Assignee chips on" : "Assignee chips off");
            }}
            className="w-5 h-5 accent-white cursor-pointer"
          />
        </label>
      </div>
    </GlassPanel>
  );
}

/* ---------------------------------------------------------------------------- */
/*                             My Password                                     */
/* ---------------------------------------------------------------------------- */
function PasswordSection() {
  const { user } = useAuth();
  const [form, setForm] = useState({ current: "", next: "", confirm: "" });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (form.next.length < 6) return toast.error("New password must be at least 6 characters");
    if (form.next !== form.confirm) return toast.error("New password and confirmation don't match");
    if (form.next === form.current) return toast.error("New password must be different from the current one");
    setBusy(true);
    try {
      await api.post("/auth/change-password", {
        current_password: form.current,
        new_password: form.next,
      });
      toast.success("Password updated. Use the new password next time you sign in.");
      setForm({ current: "", next: "", confirm: "" });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not change password");
    } finally {
      setBusy(false);
    }
  };

  return (
    <GlassPanel className="!p-6 max-w-lg" data-testid="settings-password">
      <div className="mb-5">
        <div className="text-label flex items-center gap-2"><KeyRound className="w-3 h-3" /> Account · {user?.email}</div>
        <h2 className="font-heading text-2xl text-white mt-1">Change my password</h2>
        <p className="text-sm text-slate-400 mt-1">
          Pick something at least 6 characters. This only rotates <b>your</b> account — not other staff.
        </p>
      </div>
      <form onSubmit={submit} className="space-y-4">
        <div>
          <label className="text-label block mb-1.5">Current password</label>
          <input
            data-testid="password-current"
            type="password"
            required
            autoComplete="current-password"
            value={form.current}
            onChange={(e) => setForm({ ...form, current: e.target.value })}
            className="w-full bg-black/40 border border-white/10 rounded-xl px-3 py-2.5 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-white/30"
          />
        </div>
        <div>
          <label className="text-label block mb-1.5">New password</label>
          <input
            data-testid="password-new"
            type="password"
            required
            minLength={6}
            autoComplete="new-password"
            value={form.next}
            onChange={(e) => setForm({ ...form, next: e.target.value })}
            className="w-full bg-black/40 border border-white/10 rounded-xl px-3 py-2.5 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-white/30"
          />
        </div>
        <div>
          <label className="text-label block mb-1.5">Confirm new password</label>
          <input
            data-testid="password-confirm"
            type="password"
            required
            minLength={6}
            autoComplete="new-password"
            value={form.confirm}
            onChange={(e) => setForm({ ...form, confirm: e.target.value })}
            className="w-full bg-black/40 border border-white/10 rounded-xl px-3 py-2.5 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-white/30"
          />
        </div>
        <div className="flex justify-end pt-2">
          <button
            data-testid="password-save"
            type="submit"
            disabled={busy}
            className="bg-white text-black rounded-full px-5 py-2.5 text-sm font-semibold hover:bg-slate-100 disabled:opacity-60"
          >
            {busy ? "Saving…" : "Update password"}
          </button>
        </div>
      </form>
    </GlassPanel>
  );
}

/* ---------------------------------------------------------------------------- */
/*                       Email (Resend) — per-shop sending                     */
/* ---------------------------------------------------------------------------- */
function EmailSection() {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [form, setForm] = useState({ resend_api_key: "", sender_email: "", sender_name: "" });
  const [testRecipient, setTestRecipient] = useState("");
  const [testResult, setTestResult] = useState(null); // { ok, message }
  const [showKey, setShowKey] = useState(false);
  const [dnsOpen, setDnsOpen] = useState(false);

  useEffect(() => {
    api.get("/tenants/me/email")
      .then(({ data }) => {
        setSettings(data);
        setForm({ resend_api_key: "", sender_email: data.sender_email || "", sender_name: data.sender_name || "" });
      })
      .catch(() => toast.error("Couldn't load email settings"))
      .finally(() => setLoading(false));
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      // Only send the API key field when the admin actually typed one — an
      // untouched key field should NOT clear what's already on file.
      const payload = {
        sender_email: form.sender_email,
        sender_name:  form.sender_name,
      };
      if (form.resend_api_key.trim()) payload.resend_api_key = form.resend_api_key.trim();
      const { data } = await api.patch("/tenants/me/email", payload);
      setSettings(data);
      setForm((f) => ({ ...f, resend_api_key: "" }));
      toast.success("Email settings saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const clearKey = async () => {
    if (!window.confirm("Remove your Resend API key? Emails will fall back to the WrapConsole global sender until you add one again.")) return;
    setSaving(true);
    try {
      const { data } = await api.patch("/tenants/me/email", { resend_api_key: "" });
      setSettings(data);
      toast.success("API key removed — using global sender");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't clear the key");
    } finally {
      setSaving(false);
    }
  };

  const sendTest = async () => {
    const to = (testRecipient || "").trim();
    if (!to || !to.includes("@")) {
      toast.error("Enter an email to receive the test");
      return;
    }
    setTesting(true);
    setTestResult(null);
    try {
      const { data } = await api.post("/tenants/me/email/test", { recipient: to });
      if (data?.result?.sent) {
        setTestResult({ ok: true, message: `Delivered to ${to}` });
        setSettings(data.settings);
        toast.success("Test email sent — check your inbox");
      } else {
        setTestResult({ ok: false, message: data?.result?.reason || "Send failed" });
        toast.error(data?.result?.reason || "Send failed");
      }
    } catch (e) {
      const msg = e?.response?.data?.detail || "Send failed";
      setTestResult({ ok: false, message: msg });
      toast.error(msg);
    } finally {
      setTesting(false);
    }
  };

  if (loading) {
    return <GlassPanel className="!p-6"><div className="text-slate-400 text-sm">Loading email settings…</div></GlassPanel>;
  }

  const configured = !!settings?.has_api_key;
  const verified = !!settings?.email_verified_at;

  return (
    <GlassPanel className="!p-6 space-y-6" data-testid="settings-email">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="text-label flex items-center gap-2"><Mail className="w-3 h-3" /> Automation</div>
          <h2 className="font-heading text-2xl text-white mt-1 flex items-center gap-2">
            Email
            {configured && verified && (
              <span className="pill pill-success text-[10px] flex items-center gap-1" data-testid="email-status-verified">
                <Sparkles className="w-2.5 h-2.5" /> Verified
              </span>
            )}
            {configured && !verified && (
              <span className="pill pill-warning text-[10px]" data-testid="email-status-unverified">
                Awaiting test send
              </span>
            )}
            {!configured && (
              <span className="pill pill-muted text-[10px]" data-testid="email-status-fallback">
                Using global default
              </span>
            )}
          </h2>
          <p className="text-sm text-slate-400 mt-1 max-w-xl">
            Send quotes, invoices, booking confirmations and team invites from your own domain
            (<code className="text-slate-300">quotes@wrapconsole.com</code>) via Resend — so every email lands with your branding, not ours.
          </p>
        </div>
      </div>

      {/* Setup walkthrough */}
      <div className="bg-white/[0.03] border border-white/10 rounded-2xl p-5">
        <button
          type="button"
          onClick={() => setDnsOpen((v) => !v)}
          data-testid="email-dns-toggle"
          className="w-full flex items-center justify-between gap-3 text-left"
        >
          <div>
            <div className="text-white text-sm font-medium flex items-center gap-2">
              <SettingsIcon className="w-4 h-4" /> One-time setup — verify your domain in Resend
            </div>
            <div className="text-xs text-slate-500 mt-0.5">Takes 5-15 minutes. Only needed once per domain.</div>
          </div>
          <ChevronRight className={`w-4 h-4 text-slate-400 transition ${dnsOpen ? "rotate-90" : ""}`} />
        </button>
        {dnsOpen && (
          <ol className="mt-4 space-y-3 text-sm text-slate-300 list-decimal list-inside">
            <li>
              Create a free account at{" "}
              <a href="https://resend.com" target="_blank" rel="noreferrer" className="text-emerald-300 underline">resend.com</a>.
            </li>
            <li>
              Resend dashboard → <b>Domains</b> → <b>Add Domain</b> → enter <code className="text-slate-200">wrapconsole.com</code>.
            </li>
            <li>
              Add the DNS records Resend shows you (SPF, DKIM, and optionally DMARC) at your domain registrar — Namecheap, Cloudflare, GoDaddy, etc. Click <b>Verify</b> once records are live (usually 5-15 min).
            </li>
            <li>
              Resend dashboard → <b>API Keys</b> → <b>Create API Key</b> → <b>Full access</b>. Copy the <code className="text-slate-200">re_…</code> key — it&apos;s shown only once.
            </li>
            <li>Paste it below, set your sender address, then send a test email to confirm delivery.</li>
          </ol>
        )}
      </div>

      {/* API Key */}
      <div className="space-y-3">
        <label className="text-label block">Resend API key</label>
        {configured ? (
          <div className="flex items-center gap-3 flex-wrap">
            <div className="bg-emerald-500/10 border border-emerald-400/30 rounded-xl px-4 py-2.5 text-sm text-emerald-200 font-mono tracking-wider" data-testid="email-key-masked">
              re_••••••••••{settings.key_last4}
            </div>
            <button
              type="button"
              onClick={clearKey}
              data-testid="email-clear-key"
              className="pill pill-muted text-xs !px-3 !py-1.5 hover:!bg-rose-500/10 hover:!text-rose-300 transition"
              disabled={saving}
            >
              Remove key
            </button>
          </div>
        ) : null}
        <div className="relative">
          <input
            data-testid="email-key-input"
            type={showKey ? "text" : "password"}
            autoComplete="off"
            value={form.resend_api_key}
            onChange={(e) => setForm({ ...form, resend_api_key: e.target.value })}
            placeholder={configured ? "Paste a new key to rotate (leave blank to keep current)" : "re_xxxxxxxxxxxxxxxxxxxxxxxxxxxx"}
            className="w-full bg-black/30 border border-white/10 rounded-xl px-4 py-2.5 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-emerald-400/60 font-mono tracking-wider pr-20"
          />
          <button
            type="button"
            onClick={() => setShowKey((v) => !v)}
            className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-slate-400 hover:text-white px-2 py-1 rounded"
          >
            {showKey ? "Hide" : "Show"}
          </button>
        </div>
        <p className="text-xs text-slate-500">
          Stored encrypted at the shop level. We never surface it back to the browser — only the last 4 characters.
        </p>
      </div>

      {/* Sender identity */}
      <div className="grid md:grid-cols-2 gap-4">
        <div>
          <label className="text-label block mb-1">Sender name</label>
          <input
            data-testid="email-sender-name"
            value={form.sender_name}
            onChange={(e) => setForm({ ...form, sender_name: e.target.value })}
            placeholder="WrapConsole"
            className="w-full bg-black/30 border border-white/10 rounded-xl px-4 py-2.5 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-emerald-400/60"
          />
          <p className="text-xs text-slate-500 mt-1">Shown in the recipient&apos;s inbox before the address.</p>
        </div>
        <div>
          <label className="text-label block mb-1">Sender email</label>
          <input
            data-testid="email-sender-email"
            value={form.sender_email}
            onChange={(e) => setForm({ ...form, sender_email: e.target.value })}
            placeholder="quotes@wrapconsole.com"
            className="w-full bg-black/30 border border-white/10 rounded-xl px-4 py-2.5 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-emerald-400/60"
          />
          <p className="text-xs text-slate-500 mt-1">Must use a domain you&apos;ve verified in Resend.</p>
        </div>
      </div>

      <div className="flex items-center justify-between gap-3 flex-wrap">
        <button
          type="button"
          onClick={save}
          disabled={saving}
          data-testid="email-save"
          className="pill pill-primary text-sm !px-5 !py-2.5"
        >
          {saving ? "Saving…" : "Save settings"}
        </button>
        {settings?.email_verified_at && (
          <div className="text-xs text-emerald-300" data-testid="email-last-verified">
            Last verified {new Date(settings.email_verified_at).toLocaleString()}
          </div>
        )}
      </div>

      {/* Test send */}
      <div className="border-t border-white/5 pt-5 space-y-3">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <div className="text-white text-sm font-medium flex items-center gap-2">
              <Bell className="w-4 h-4" /> Send a test email
            </div>
            <div className="text-xs text-slate-500 mt-0.5">
              Fires using your current settings — the exact path a quote/invoice email will take.
            </div>
          </div>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <input
            data-testid="email-test-recipient"
            value={testRecipient}
            onChange={(e) => setTestRecipient(e.target.value)}
            placeholder="your@email.com"
            className="flex-1 min-w-[220px] bg-black/30 border border-white/10 rounded-xl px-4 py-2.5 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-emerald-400/60"
          />
          <button
            type="button"
            onClick={sendTest}
            disabled={testing}
            data-testid="email-send-test"
            className="pill pill-success text-sm !px-5 !py-2.5 flex items-center gap-2"
          >
            {testing ? "Sending…" : (<><Mail className="w-3.5 h-3.5" /> Send test</>)}
          </button>
        </div>
        {testResult && (
          <div
            data-testid={testResult.ok ? "email-test-ok" : "email-test-fail"}
            className={`rounded-xl px-4 py-3 text-sm ${
              testResult.ok
                ? "bg-emerald-500/10 border border-emerald-400/30 text-emerald-200"
                : "bg-rose-500/10 border border-rose-400/30 text-rose-200"
            }`}
          >
            {testResult.ok ? "✅ " : "❌ "}
            {testResult.message}
          </div>
        )}
      </div>
    </GlassPanel>
  );
}

/* ---------------------------------------------------------------------------- */
/*                        SMS Reminders (Coming Soon)                          */
/* ---------------------------------------------------------------------------- */
function SmsRemindersSection() {
  // Static values only — this whole section is a Coming Soon preview and
  // must not mutate any state or fire any API calls.
  const enabled = false;
  const leadHours = 24;
  const template =
    "Hi {{first_name}}, just a reminder your {{service}} on your {{vehicle}} is booked in for {{scheduled_date}}. Reply Y to confirm or call us to reschedule. — WrapConsole";

  return (
    <GlassPanel className="!p-6 space-y-5" data-testid="settings-sms">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="text-label flex items-center gap-2"><MessageSquare className="w-3 h-3" /> Automation</div>
          <h2 className="font-heading text-2xl text-white mt-1 flex items-center gap-2">
            SMS Reminders
            <span className="pill pill-warning text-[10px] flex items-center gap-1" data-testid="sms-coming-soon">
              <Sparkles className="w-2.5 h-2.5" /> Coming soon
            </span>
          </h2>
          <p className="text-sm text-slate-400 mt-1 max-w-xl">
            Auto-text customers 24 hours before their booking, then flag no-shows on the Dashboard if they don&apos;t
            confirm. Powered by Twilio — flip the switch once your account is connected.
          </p>
        </div>
      </div>

      {/* Preview card — visual proof of concept while feature is being wired up */}
      <div className="bg-white/[0.03] border border-white/10 rounded-2xl p-4 space-y-4 relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none bg-gradient-to-br from-transparent via-transparent to-amber-500/[0.04]" />
        <div className="flex items-center justify-between gap-3 flex-wrap relative">
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-2xl border flex items-center justify-center ${enabled ? "bg-emerald-500/15 border-emerald-400/30 text-emerald-300" : "bg-white/5 border-white/10 text-slate-400"}`}>
              <Bell className="w-4 h-4" />
            </div>
            <div>
              <div className="text-white text-sm font-medium">Booking reminders</div>
              <div className="text-xs text-slate-500">Send a text 24h before each booked job</div>
            </div>
          </div>
          <button
            data-testid="sms-toggle"
            type="button"
            onClick={() => toast.info("Available once Twilio is connected")}
            className={`relative w-12 h-6 rounded-full border transition ${enabled ? "bg-emerald-500/70 border-emerald-400/50" : "bg-white/5 border-white/15"}`}
            aria-pressed={enabled}
          >
            <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-white transition ${enabled ? "left-6" : "left-0.5"}`} />
          </button>
        </div>

        <div className="grid md:grid-cols-2 gap-3 relative">
          <label>
            <span className="text-label block mb-1 flex items-center gap-1"><Clock className="w-3 h-3" /> Lead time before booking</span>
            <select
              data-testid="sms-lead-hours"
              value={leadHours}
              onChange={() => {}}
              disabled
              className="w-full bg-black/40 border border-white/10 rounded-xl px-3 py-2 text-sm text-white opacity-70 cursor-not-allowed"
            >
              {[6, 12, 24, 48, 72].map((h) => (
                <option key={h} value={h} className="bg-neutral-900">{h} hours</option>
              ))}
            </select>
          </label>
          <label>
            <span className="text-label block mb-1">From number</span>
            <input
              data-testid="sms-from-number"
              value=""
              disabled
              placeholder="+61 4XX XXX XXX (Twilio number required)"
              className="w-full bg-black/40 border border-white/10 rounded-xl px-3 py-2 text-sm text-slate-500 placeholder-slate-600 opacity-70 cursor-not-allowed"
            />
          </label>
        </div>

        <label className="block relative">
          <span className="text-label block mb-1">Message template</span>
          <textarea
            data-testid="sms-template"
            value={template}
            onChange={() => {}}
            disabled
            rows={4}
            className="w-full bg-black/40 border border-white/10 rounded-xl px-3 py-2 text-sm text-slate-300 opacity-80 cursor-not-allowed font-mono"
          />
          <div className="text-[10px] text-slate-500 mt-1">
            Variables: <code className="text-slate-300">{`{{first_name}}`}</code>{" "}
            <code className="text-slate-300">{`{{vehicle}}`}</code>{" "}
            <code className="text-slate-300">{`{{service}}`}</code>{" "}
            <code className="text-slate-300">{`{{scheduled_date}}`}</code>
          </div>
        </label>
      </div>

      {/* Roadmap ribbon */}
      <div className="bg-amber-500/5 border border-amber-400/20 rounded-2xl p-4 text-xs text-amber-100 space-y-2">
        <div className="flex items-center gap-2 font-medium text-amber-200">
          <Sparkles className="w-3.5 h-3.5" /> On the roadmap
        </div>
        <ul className="list-disc list-inside space-y-1 text-amber-100/80">
          <li>Twilio Account SID + Auth Token + From-number connection</li>
          <li>Automated worker that scans booked jobs each hour and sends reminders at the chosen lead time</li>
          <li>Inbound reply parsing — Y confirms; anything else pings the receptionist</li>
          <li>No-show flag on the Dashboard when the booking date passes with no reply and no clock-in</li>
        </ul>
      </div>
    </GlassPanel>
  );
}

/* ---------------------------------------------------------------------------- */
/*                                  Accounts                                    */
/* ---------------------------------------------------------------------------- */
function AccountsSection() {
  const { user: current } = useAuth();
  const [members, setMembers] = useState([]);
  const [invites, setInvites] = useState([]);
  const [inviting, setInviting] = useState(false);
  const [editing, setEditing] = useState(null);
  const [resetting, setResetting] = useState(null);
  const [profiling, setProfiling] = useState(null);
  // Loading & error are tracked separately from `members.length` so the
  // empty table doesn't lie — previously a successful fetch returning
  // zero rows (or a silent failure) rendered "Loading team..." forever.
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [repairing, setRepairing] = useState(false);

  const bulkRepair = async () => {
    if (!window.confirm("Sweep for any users that belong to this shop but aren't showing up here? Safe to run — it only ADDS missing membership links, never removes anyone.")) return;
    setRepairing(true);
    try {
      const { data } = await api.post("/tenants/me/members/repair");
      const added = data?.added || 0;
      const patched = data?.patched || 0;
      if (added === 0 && patched === 0) {
        toast.success("Everything looks tidy — no missing memberships to repair");
      } else {
        toast.success(`Fixed up the team roster — ${added} re-linked, ${patched} role-patched`);
      }
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Repair sweep failed");
    } finally {
      setRepairing(false);
    }
  };

  const load = async () => {
    setLoadError("");
    try {
      const [m, i] = await Promise.all([
        api.get("/tenants/me/members"),
        api.get("/tenants/me/invites").catch(() => ({ data: [] })),
      ]);
      setMembers(m.data || []);
      setInvites(i.data || []);
    } catch (e) {
      setMembers([]);
      setInvites([]);
      // Surface the backend's own error message when it provides one so
      // the user has a chance of diagnosing it (403 wrong-role, 404 no
      // active tenant, 503 db unreachable) instead of just a dead
      // spinner.
      const msg = e?.response?.data?.detail
        || e?.message
        || "Couldn't load team — check your connection and reload.";
      setLoadError(typeof msg === "string" ? msg : "Couldn't load team.");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const remove = async (u) => {
    if (u.id === current?.id) { toast.error("You can't remove yourself from the shop"); return; }
    if (u.is_tenant_owner) { toast.error("You can't remove the shop owner"); return; }
    if (!window.confirm(`Remove ${u.name || u.email} from this shop? Their account will stay but they'll lose access to this shop's data.`)) return;
    try {
      await api.delete(`/tenants/me/members/${u.id}`);
      toast.success("Member removed");
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const revokeInvite = async (inv) => {
    if (!window.confirm(`Revoke invitation for ${inv.email}?`)) return;
    try {
      await api.delete(`/tenants/me/invites/${inv.id}`);
      toast.success("Invitation revoked");
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const copyInviteLink = async (inv) => {
    try {
      const base = window.location.origin;
      const link = inv.accept_url?.startsWith("http") ? inv.accept_url : `${base}${inv.accept_url}`;
      await navigator.clipboard.writeText(link);
      toast.success("Invite link copied");
    } catch { toast.error("Couldn't copy link"); }
  };

  const adminCount = members.filter((u) => u.role === "admin").length;

  return (
    <GlassPanel className="!p-6" data-testid="settings-accounts">
      <div className="flex items-start justify-between gap-4 mb-5">
        <div>
          <div className="text-label flex items-center gap-2"><Users className="w-3 h-3" /> Team access</div>
          <h2 className="font-heading text-2xl text-white mt-1">Team members</h2>
          <p className="text-xs text-slate-400 mt-1 max-w-md">
            Invite staff by email — they'll get a link to set their own password.
            Change roles any time from this screen; permissions live in <b>Roles & Permissions</b>.
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0 flex-wrap">
          <button
            data-testid="accounts-repair-btn"
            onClick={bulkRepair}
            disabled={repairing}
            title="Scan for installers who belong to this shop but aren't showing up here (e.g. legacy accounts from before the multi-shop upgrade)"
            className="flex items-center gap-2 bg-white/5 hover:bg-white/10 border border-white/10 text-slate-200 px-3.5 py-2 rounded-full text-xs transition disabled:opacity-50"
          >
            <RotateCcw className={`w-3.5 h-3.5 ${repairing ? "animate-spin" : ""}`} />
            {repairing ? "Scanning…" : "Scan for missing members"}
          </button>
          <button
            data-testid="accounts-invite-btn"
            onClick={() => setInviting(true)}
            className="flex items-center gap-2 bg-white text-black px-4 py-2 rounded-full text-sm font-semibold hover:bg-slate-100"
          >
            <Mail className="w-4 h-4" /> Invite member
          </button>
        </div>
      </div>

      <div className="rounded-2xl border border-white/5 overflow-x-auto">
        <table className="w-full min-w-[600px]">
          <thead className="bg-white/[0.02] text-[10px] uppercase tracking-widest text-slate-500">
            <tr>
              <th className="text-left px-5 py-3">Name</th>
              <th className="text-left px-5 py-3">Email</th>
              <th className="text-left px-5 py-3">Role</th>
              <th className="text-right px-5 py-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={4} className="px-5 py-8 text-center text-slate-500 italic text-sm" data-testid="accounts-loading">Loading team…</td></tr>
            )}
            {!loading && loadError && (
              <tr><td colSpan={4} className="px-5 py-8 text-center" data-testid="accounts-load-error">
                <div className="inline-flex items-start gap-2 text-rose-200 text-sm bg-rose-500/[0.08] border border-rose-400/30 rounded-xl px-4 py-2">
                  <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
                  <div className="text-left">
                    <div className="font-medium">Couldn&apos;t load team</div>
                    <div className="text-xs text-rose-200/80">{loadError}</div>
                    <button
                      type="button"
                      onClick={load}
                      className="mt-1 text-xs underline hover:text-white"
                      data-testid="accounts-load-retry"
                    >Try again</button>
                  </div>
                </div>
              </td></tr>
            )}
            {!loading && !loadError && members.length === 0 && (
              <tr><td colSpan={4} className="px-5 py-8 text-center text-slate-500 italic text-sm" data-testid="accounts-empty">
                No team members yet. Click <b>Invite member</b> above to add your first installer.
              </td></tr>
            )}
            {members.map((u) => {
              const isSelf = u.id === current?.id;
              const isLastAdmin = u.role === "admin" && adminCount <= 1;
              const protect = u.is_tenant_owner;
              return (
                <tr key={u.id} data-testid={`account-row-${u.id}`} className="border-t border-white/[0.04] hover:bg-white/[0.03]">
                  <td className="px-5 py-3 text-white">
                    <div className="flex items-center gap-2">
                      {(u.role === "installer" || u.role === "assistant") && u.color && (
                        <span
                          data-testid={`account-color-swatch-${u.id}`}
                          title={`Accent colour ${u.color}`}
                          className="w-3 h-3 rounded-sm border border-white/20 shrink-0"
                          style={{ background: u.color }}
                        />
                      )}
                      <span>{u.name || "—"}</span>
                      {isSelf && <span className="pill pill-muted text-[9px] ml-1">YOU</span>}
                      {u.is_tenant_owner && <span className="pill pill-info text-[9px] ml-1" title="Shop owner">OWNER</span>}
                    </div>
                  </td>
                  <td className="px-5 py-3 text-slate-300 text-sm flex items-center gap-1.5"><Mail className="w-3 h-3 text-slate-500" />{u.email}</td>
                  <td className="px-5 py-3">
                    <span className={`pill ${
                      u.role === "admin" ? "pill-info"
                      : u.role === "office" ? "pill-warning"
                      : u.role === "assistant" ? "pill-muted"
                      : "pill-muted"
                    } flex items-center gap-1 w-fit`}>
                      {u.role === "admin" && <Shield className="w-3 h-3" />}
                      {u.role}
                    </span>
                    {(u.role === "installer" || u.role === "assistant") && (
                      <div className="text-[10px] text-slate-500 mt-1">
                        {Array.isArray(u.capable_stages) && u.capable_stages.length > 0
                          ? `${u.capable_stages.length} stage${u.capable_stages.length === 1 ? "" : "s"} qualified`
                          : "No stages set"}
                      </div>
                    )}
                  </td>
                  <td className="px-5 py-3 text-right">
                    <div className="inline-flex gap-1.5">
                      {(u.role === "installer" || u.role === "assistant") && (
                        <button
                          data-testid={`account-profile-${u.id}`}
                          onClick={() => setProfiling(u)}
                          title="Edit profile & job stages"
                          className="p-1.5 rounded-lg bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-300 flex items-center gap-1 text-xs px-2"
                        >
                          <UserIcon className="w-3 h-3" /> Profile
                        </button>
                      )}
                      <button
                        data-testid={`account-reset-${u.id}`}
                        onClick={() => setResetting(u)}
                        title="Reset password"
                        className="p-1.5 rounded-lg bg-amber-500/10 hover:bg-amber-500/20 text-amber-300 flex items-center gap-1 text-xs px-2"
                      >
                        <KeyRound className="w-3 h-3" /> Reset
                      </button>
                      <button
                        data-testid={`account-edit-${u.id}`}
                        onClick={() => setEditing(u)}
                        title="Edit role"
                        className="p-1.5 rounded-lg bg-white/5 hover:bg-white/10 text-slate-300"
                      >
                        <Pencil className="w-3.5 h-3.5" />
                      </button>
                      <button
                        data-testid={`account-delete-${u.id}`}
                        onClick={() => remove(u)}
                        disabled={isSelf || isLastAdmin || protect}
                        title={protect ? "You can't remove the shop owner" : isSelf ? "You can't remove yourself" : isLastAdmin ? "At least one admin must remain" : "Remove from this shop"}
                        className={`p-1.5 rounded-lg ${isSelf || isLastAdmin || protect ? "bg-white/[0.02] text-slate-600 cursor-not-allowed" : "bg-white/5 hover:bg-red-500/20 text-slate-300 hover:text-red-300"}`}
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Pending invitations */}
      {invites.length > 0 && (
        <div className="mt-6" data-testid="pending-invites-section">
          <div className="text-label flex items-center gap-2 mb-2"><Mail className="w-3 h-3" /> Pending invitations</div>
          <div className="rounded-2xl border border-amber-500/20 bg-amber-500/5 overflow-x-auto">
            <table className="w-full min-w-[600px]">
              <thead className="text-[10px] uppercase tracking-widest text-slate-500">
                <tr>
                  <th className="text-left px-5 py-3">Email</th>
                  <th className="text-left px-5 py-3">Role</th>
                  <th className="text-left px-5 py-3">Invited by</th>
                  <th className="text-left px-5 py-3">Status</th>
                  <th className="text-right px-5 py-3">Actions</th>
                </tr>
              </thead>
              <tbody>
                {invites.map((inv) => (
                  <tr key={inv.id} data-testid={`invite-row-${inv.id}`} className="border-t border-white/[0.04]">
                    <td className="px-5 py-3 text-slate-200 text-sm">{inv.email}</td>
                    <td className="px-5 py-3 text-sm">
                      <span className="pill pill-muted">{inv.role}</span>
                    </td>
                    <td className="px-5 py-3 text-slate-400 text-xs">{inv.invited_by || "—"}</td>
                    <td className="px-5 py-3">
                      <span className={`pill text-[10px] ${inv.status === "pending" ? "pill-warning" : "pill-muted"}`}>
                        {inv.status}
                      </span>
                    </td>
                    <td className="px-5 py-3 text-right">
                      <div className="inline-flex gap-1.5">
                        <button
                          data-testid={`invite-copy-${inv.id}`}
                          onClick={() => copyInviteLink(inv)}
                          title="Copy invitation link"
                          className="p-1.5 rounded-lg bg-white/5 hover:bg-white/10 text-slate-300 text-xs px-2"
                        >
                          Copy link
                        </button>
                        <button
                          data-testid={`invite-revoke-${inv.id}`}
                          onClick={() => revokeInvite(inv)}
                          title="Revoke invitation"
                          className="p-1.5 rounded-lg bg-white/5 hover:bg-red-500/20 text-slate-300 hover:text-red-300"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {inviting && <InviteMemberModal onClose={() => setInviting(false)} onSaved={load} />}
      {editing && <EditAccountModal user={editing} onClose={() => setEditing(null)} onSaved={load} />}
      {resetting && <ResetPasswordModal user={resetting} onClose={() => setResetting(null)} />}
      {profiling && (
        <StaffProfileModal
          user={profiling}
          onClose={() => setProfiling(null)}
          onSaved={load}
        />
      )}
    </GlassPanel>
  );
}

function InviteMemberModal({ onClose, onSaved }) {
  const roles = useAssignableRoles();
  const [form, setForm] = useState({ email: "", role: "installer" });
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.email.trim()) { toast.error("Email is required"); return; }
    setSaving(true);
    try {
      const { data } = await api.post("/tenants/me/invites", form);
      if (data.email_sent) {
        toast.success(`Invitation emailed to ${form.email}`);
      } else {
        // No email configured — copy the link to clipboard as a fallback.
        try {
          const base = window.location.origin;
          const link = data.accept_url?.startsWith("http") ? data.accept_url : `${base}${data.accept_url}`;
          await navigator.clipboard.writeText(link);
          toast.success("Invite created — link copied to clipboard (email not configured)");
        } catch {
          toast.success("Invite created — copy the link from the pending list");
        }
      }
      onSaved?.();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create invitation");
    } finally {
      setSaving(false);
    }
  };

  return (
    <FormModal title="Invite member" onClose={onClose} onSubmit={submit}>
      <Field label="Email" type="email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} testId="invite-email" required />
      <SelectField label="Role" value={form.role} onChange={(v) => setForm({ ...form, role: v })} options={roles} testId="invite-role" />
      <p className="text-[11px] text-slate-500">
        They'll receive an email with a link to set their password and join your shop. The link expires in 7 days.
      </p>
    </FormModal>
  );
}

function EditAccountModal({ user, onClose, onSaved }) {
  const roles = useAssignableRoles();
  const [role, setRole] = useState(user.role);
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.patch(`/tenants/me/members/${user.id}`, { role });
      toast.success("Role updated");
      onSaved?.();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update");
    } finally {
      setSaving(false);
    }
  };

  return (
    <FormModal title={`Edit role — ${user.name || user.email}`} onClose={onClose} onSubmit={submit}>
      <div className="rounded-xl bg-black/30 border border-white/10 p-3 space-y-1 text-xs">
        <div className="text-slate-400">Name: <span className="text-white">{user.name || "—"}</span></div>
        <div className="text-slate-400">Email: <span className="text-white">{user.email}</span></div>
      </div>
      <SelectField label="Role in this shop" value={role} onChange={setRole} options={roles} testId="account-edit-role" />
      <p className="text-[11px] text-slate-500">
        Changing a role here only affects this shop. If this person belongs to multiple shops, their role in the others stays the same.
      </p>
    </FormModal>
  );
}

function ResetPasswordModal({ user, onClose }) {
  const [password, setPassword] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 6) { toast.error("Password must be at least 6 characters"); return; }
    setSaving(true);
    try {
      await api.post(`/users/${user.id}/password`, { password });
      toast.success(`Password reset for ${user.name || user.email}`);
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <FormModal title={`Reset password — ${user.name || user.email}`} onClose={onClose} onSubmit={submit}>
      <Field label="New password" type="password" value={password} onChange={setPassword} testId="account-reset-password" required />
      <p className="text-[11px] text-slate-500">
        Share the new password with them securely. Any tokens they already hold will keep working until their next login — ask them to sign out and back in if you need immediate revocation.
      </p>
    </FormModal>
  );
}


/* ---------------------------------------------------------------------------- */
/*                    Staff Profile — mini contact sheet + stages               */
/* ---------------------------------------------------------------------------- */
function StaffProfileModal({ user, onClose, onSaved }) {
  const [phone, setPhone] = useState(user.phone || "");
  const [bio, setBio] = useState(user.bio || "");
  const [stages, setStages] = useState(new Set(user.capable_stages || []));
  const [color, setColor] = useState(user.color || "");
  const [capacityRaw, setCapacityRaw] = useState(
    user.capacity_hours_per_week === null || user.capacity_hours_per_week === undefined
      ? ""
      : String(user.capacity_hours_per_week)
  );
  const [saving, setSaving] = useState(false);

  const toggle = (val) => {
    setStages((prev) => {
      const next = new Set(prev);
      if (next.has(val)) next.delete(val); else next.add(val);
      return next;
    });
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.put(`/users/${user.id}/profile`, {
        phone: phone.trim(),
        bio: bio.trim(),
        capable_stages: Array.from(stages),
        color: color || "",
        // Empty string = "clear override, fall back to shop-wide capacity".
        // A numeric value (including 0 for on-leave) is sent verbatim.
        capacity_hours_per_week: capacityRaw.trim() === "" ? null : Number(capacityRaw),
      });
      toast.success(`Profile saved for ${user.name || user.email}`);
      onSaved?.();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save profile");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/60 backdrop-blur-md flex items-center justify-center p-4"
      data-testid="staff-profile-modal"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <form
        onSubmit={submit}
        className="glass-strong rounded-3xl p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto space-y-5"
      >
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="text-label flex items-center gap-2">
              <UserIcon className="w-3 h-3" /> Staff profile
            </div>
            <h2 className="font-heading text-2xl text-white mt-1">{user.name || user.email}</h2>
            <div className="text-xs text-slate-400 mt-1 flex items-center gap-2">
              <Mail className="w-3 h-3" /> {user.email}
              <span className={`pill ${user.role === "assistant" ? "pill-muted" : "pill-info"} text-[9px]`}>{user.role}</span>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            data-testid="staff-profile-close"
            className="p-1.5 rounded-full bg-white/5 hover:bg-white/10 text-slate-300"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Mini contact sheet */}
        <div className="rounded-2xl bg-black/30 border border-white/10 p-4 space-y-3">
          <div className="text-label flex items-center gap-1.5"><Phone className="w-3 h-3" /> Contact details</div>
          <label className="block">
            <span className="text-[10px] uppercase tracking-widest text-slate-500 block mb-1">Phone</span>
            <input
              type="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="+61 400 000 000"
              data-testid="staff-profile-phone"
              className="w-full bg-black/40 border border-white/10 rounded-xl px-3 py-2 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-white/30"
            />
          </label>
          <label className="block">
            <span className="text-[10px] uppercase tracking-widest text-slate-500 block mb-1">Bio / notes</span>
            <textarea
              value={bio}
              onChange={(e) => setBio(e.target.value)}
              rows={3}
              placeholder="Years of experience, certifications, preferred vehicle types…"
              data-testid="staff-profile-bio"
              className="w-full bg-black/40 border border-white/10 rounded-xl px-3 py-2 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-white/30"
            />
          </label>
        </div>

        {/* Accent colour picker — powers calendar bar tinting */}
        <div className="rounded-2xl bg-black/30 border border-white/10 p-4 space-y-3" data-testid="staff-profile-color-panel">
          <div className="flex items-start justify-between gap-3">
            <div>
              <div className="text-label flex items-center gap-1.5"><Palette className="w-3 h-3" /> Personal accent colour</div>
              <p className="text-xs text-slate-400 mt-1">
                Used to tint this person&apos;s calendar bars, timeline chips, and future
                assignment badges. Pick something distinct from the rest of the crew.
              </p>
            </div>
            {color && (
              <button
                type="button"
                onClick={() => setColor("")}
                data-testid="staff-profile-color-clear"
                className="text-[10px] uppercase tracking-widest text-slate-400 hover:text-white"
              >
                Clear
              </button>
            )}
          </div>
          <div className="grid grid-cols-6 sm:grid-cols-12 gap-2">
            {INSTALLER_COLORS.map((c) => {
              const active = color?.toLowerCase() === c.hex.toLowerCase();
              return (
                <button
                  key={c.hex}
                  type="button"
                  onClick={() => setColor(c.hex)}
                  data-testid={`staff-profile-color-${c.hex.replace('#','')}`}
                  title={c.name}
                  className={`aspect-square rounded-xl border-2 transition ${active ? "ring-2 ring-white/60 border-white/40" : "border-white/10 hover:border-white/30"}`}
                  style={{ background: c.hex }}
                />
              );
            })}
          </div>
          <div className="flex items-center gap-3 pt-1">
            <div className="text-[10px] uppercase tracking-widest text-slate-500">Preview</div>
            <div
              className="flex-1 h-6 rounded-lg border border-white/10 flex items-center px-3 text-[11px] text-white"
              style={{ background: color || "rgba(255,255,255,0.05)" }}
              data-testid="staff-profile-color-preview"
            >
              {color ? `${user.name || user.email} · sample event bar` : "No colour selected — using default status colour"}
            </div>
          </div>
        </div>

        {/* Weekly capacity override — powers per-installer utilization
            bars on the calendar. Blank = use shop-wide workday_hours total. */}
        <div className="rounded-2xl bg-black/30 border border-white/10 p-4 space-y-2" data-testid="staff-profile-capacity-panel">
          <div className="text-label flex items-center gap-1.5"><Timer className="w-3 h-3" /> Weekly capacity (hours)</div>
          <div className="flex items-center gap-3">
            <input
              type="number"
              min="0"
              max="80"
              step="0.5"
              value={capacityRaw}
              onChange={(e) => setCapacityRaw(e.target.value)}
              placeholder="Auto (use shop hours)"
              data-testid="staff-profile-capacity"
              className="w-40 bg-black/40 border border-white/10 rounded-xl px-3 py-2 text-sm text-white focus:outline-none focus:border-white/30"
            />
            <span className="text-xs text-slate-500 shrink-0">hrs / week</span>
            {capacityRaw.trim() !== "" && (
              <button
                type="button"
                onClick={() => setCapacityRaw("")}
                data-testid="staff-profile-capacity-clear"
                className="text-[10px] uppercase tracking-widest text-slate-400 hover:text-white ml-auto"
              >
                Clear
              </button>
            )}
          </div>
          <p className="text-[11px] text-slate-400">
            Blank means this person uses the shop-wide working-hours total
            (sum of your weekly workday hours). Set a smaller number for
            part-timers or 0 to mark them on leave — the calendar workload
            bar will re-scale their utilization accordingly.
          </p>
        </div>

        {/* Job stages */}
        <div className="rounded-2xl bg-black/30 border border-white/10 p-4 space-y-3">
          <div className="flex items-start justify-between gap-3">
            <div>
              <div className="text-label flex items-center gap-1.5"><Wrench className="w-3 h-3" /> Qualified job stages</div>
              <p className="text-xs text-slate-400 mt-1">
                Tick every stage this staff member is trained to perform. When you build a crew schedule the picker
                highlights everyone qualified for the picked stage — a car booked for wash down only shows people who can do that.
              </p>
            </div>
            <div className="text-[10px] uppercase tracking-widest text-slate-500 shrink-0">
              {stages.size} / {JOB_STAGES.length}
            </div>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
            {JOB_STAGES.map((s) => {
              const active = stages.has(s.value);
              return (
                <button
                  key={s.value}
                  type="button"
                  data-testid={`stage-${s.value}`}
                  onClick={() => toggle(s.value)}
                  className={`text-xs rounded-xl px-3 py-2 border transition text-left ${
                    active
                      ? "bg-emerald-500/15 border-emerald-400/40 text-emerald-100"
                      : "bg-white/[0.02] border-white/10 text-slate-400 hover:bg-white/[0.05]"
                  }`}
                >
                  <span className={`inline-block w-3 h-3 rounded border mr-2 align-middle ${
                    active ? "bg-emerald-400 border-emerald-300" : "border-white/30"
                  }`} />
                  {s.label}
                </button>
              );
            })}
          </div>
        </div>

        <div className="flex justify-end gap-2 pt-2">
          <button
            type="button"
            onClick={onClose}
            className="px-5 py-2.5 rounded-full bg-white/5 border border-white/10 text-sm text-slate-300 hover:bg-white/10"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={saving}
            data-testid="staff-profile-save"
            className="px-5 py-2.5 rounded-full bg-white text-black text-sm font-semibold hover:bg-slate-100 disabled:opacity-60"
          >
            {saving ? "Saving…" : "Save profile"}
          </button>
        </div>
      </form>
    </div>
  );
}


/* ---------------------------------------------------------------------------- */
/* Auto-archive tuner — admin-only. Controls the background sweeper that
   moves stale leads / quotes to Archived after the configured window. */
/* ---------------------------------------------------------------------------- */
function AutoArchiveSection() {
  const [values, setValues] = useState({ quote_expiry_days: 30, lead_expiry_days: 60 });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [running, setRunning] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/pipeline/expiry-settings");
      setValues({
        quote_expiry_days: Number(data.quote_expiry_days) || 30,
        lead_expiry_days: Number(data.lead_expiry_days) || 60,
      });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not load expiry settings");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const save = async (e) => {
    e.preventDefault();
    const q = Number(values.quote_expiry_days);
    const l = Number(values.lead_expiry_days);
    if (!Number.isFinite(q) || q < 1 || q > 365) {
      toast.error("Quote expiry must be between 1 and 365 days"); return;
    }
    if (!Number.isFinite(l) || l < 1 || l > 365) {
      toast.error("Lead expiry must be between 1 and 365 days"); return;
    }
    setSaving(true);
    try {
      await api.put("/pipeline/expiry-settings", {
        quote_expiry_days: q,
        lead_expiry_days: l,
      });
      toast.success("Auto-archive window updated");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not save");
    } finally {
      setSaving(false);
    }
  };

  const runNow = async () => {
    if (!window.confirm("Run the auto-archive sweep now? Any leads/quotes past the window will be archived immediately.")) return;
    setRunning(true);
    try {
      const { data } = await api.post("/pipeline/run-expiry-sweep");
      const arched = (data?.leads_archived || 0) + (data?.quotes_archived || 0);
      toast.success(arched > 0 ? `Sweep complete — ${arched} archived` : "Sweep complete — nothing was overdue");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Sweep failed");
    } finally {
      setRunning(false);
    }
  };

  if (loading) {
    return (
      <GlassPanel className="!p-6" data-testid="auto-archive-loading">
        <p className="text-slate-400 text-sm italic">Loading auto-archive settings…</p>
      </GlassPanel>
    );
  }

  return (
    <GlassPanel className="!p-6" data-testid="auto-archive-section">
      <div className="mb-5">
        <h2 className="font-heading text-2xl text-white flex items-center gap-2">
          <Archive className="w-5 h-5" /> Auto-archive window
        </h2>
        <p className="text-sm text-slate-400 mt-1">
          Anything that sits in the Leads or Quotes tab past its window gets
          automatically moved to Archived. Sweep runs once an hour in the
          background. Adjust the thresholds to suit your team&apos;s rhythm.
        </p>
      </div>

      <form onSubmit={save} className="space-y-4 max-w-md">
        <div>
          <label className="text-label block mb-1.5">Quote expiry (days)</label>
          <input
            type="number"
            min="1"
            max="365"
            step="1"
            value={values.quote_expiry_days}
            onChange={(e) => setValues({ ...values, quote_expiry_days: e.target.value })}
            data-testid="auto-archive-quote-days"
            className="w-full bg-black/40 border border-white/10 rounded-xl px-3 py-2.5 text-sm text-white focus:outline-none focus:border-white/30"
          />
          <p className="text-[11px] text-slate-500 mt-1">
            Quotes untouched for this many days will be archived as lost.
          </p>
        </div>

        <div>
          <label className="text-label block mb-1.5">Lead expiry (days)</label>
          <input
            type="number"
            min="1"
            max="365"
            step="1"
            value={values.lead_expiry_days}
            onChange={(e) => setValues({ ...values, lead_expiry_days: e.target.value })}
            data-testid="auto-archive-lead-days"
            className="w-full bg-black/40 border border-white/10 rounded-xl px-3 py-2.5 text-sm text-white focus:outline-none focus:border-white/30"
          />
          <p className="text-[11px] text-slate-500 mt-1">
            Leads without a follow-up for this many days will be archived as lost.
          </p>
        </div>

        <div className="flex flex-wrap gap-2 pt-2">
          <button
            type="submit"
            disabled={saving}
            data-testid="auto-archive-save"
            className="px-5 py-2.5 rounded-full bg-white text-black text-sm font-semibold hover:bg-slate-100 disabled:opacity-60"
          >
            {saving ? "Saving…" : "Save window"}
          </button>
          <button
            type="button"
            onClick={runNow}
            disabled={running}
            data-testid="auto-archive-run-now"
            className="px-5 py-2.5 rounded-full bg-white/5 border border-white/10 text-slate-200 text-sm hover:bg-white/10 disabled:opacity-60"
          >
            {running ? "Running…" : "Run sweep now"}
          </button>
        </div>
      </form>
    </GlassPanel>
  );
}

/* ---------------------------------------------------------------------------- */
/* Working Hours — per-day cap driving the Calendar workload bars. Weekend
   defaults to 0 (no unpaid weekend counting). Non-zero on Sat/Sun explicitly
   opts the shop into weekend hours. */
/* ---------------------------------------------------------------------------- */
const DAYS_OF_WEEK = [
  { key: "mon", label: "Monday" },
  { key: "tue", label: "Tuesday" },
  { key: "wed", label: "Wednesday" },
  { key: "thu", label: "Thursday" },
  { key: "fri", label: "Friday" },
  { key: "sat", label: "Saturday" },
  { key: "sun", label: "Sunday" },
];
const DEFAULT_WORKDAY_HOURS_UI = { mon: 8, tue: 8, wed: 8, thu: 8, fri: 8, sat: 0, sun: 0 };

function WorkdayHoursSection() {
  const [values, setValues] = useState(DEFAULT_WORKDAY_HOURS_UI);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/settings/workday-hours");
      const next = {};
      for (const d of DAYS_OF_WEEK) next[d.key] = Number(data[d.key] ?? DEFAULT_WORKDAY_HOURS_UI[d.key]);
      setValues(next);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not load working hours");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const total = DAYS_OF_WEEK.reduce((s, d) => s + (Number(values[d.key]) > 0 ? Number(values[d.key]) : 0), 0);

  const save = async (e) => {
    e.preventDefault();
    for (const d of DAYS_OF_WEEK) {
      const n = Number(values[d.key]);
      if (!Number.isFinite(n) || n < 0 || n > 24) {
        toast.error(`${d.label} hours must be between 0 and 24`); return;
      }
    }
    setSaving(true);
    try {
      const payload = {};
      for (const d of DAYS_OF_WEEK) payload[d.key] = Number(values[d.key]);
      await api.put("/settings/workday-hours", payload);
      toast.success("Working hours updated — calendar workload bars will refresh on reload");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not save");
    } finally {
      setSaving(false);
    }
  };

  const reset = () => setValues({ ...DEFAULT_WORKDAY_HOURS_UI });

  if (loading) {
    return (
      <GlassPanel className="!p-6" data-testid="workday-hours-loading">
        <p className="text-slate-400 text-sm italic">Loading working hours…</p>
      </GlassPanel>
    );
  }

  return (
    <GlassPanel className="!p-6" data-testid="workday-hours-section">
      <div className="mb-5">
        <h2 className="font-heading text-2xl text-white flex items-center gap-2">
          <Timer className="w-5 h-5" /> Working hours
        </h2>
        <p className="text-sm text-slate-400 mt-1">
          How many hours the shop is booked for each day. Drives the workload
          bars on the calendar. Weekend defaults to 0 &mdash; increase a day
          to book overtime or weekend work.
        </p>
      </div>

      <form onSubmit={save} className="space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          {DAYS_OF_WEEK.map((d) => {
            const n = Number(values[d.key]);
            const isOff = !n;
            const isOvertime = n > 8;
            return (
              <label
                key={d.key}
                className={`rounded-2xl border p-3 space-y-1 transition ${
                  isOff
                    ? "bg-white/[0.02] border-white/10"
                    : isOvertime
                      ? "bg-amber-500/[0.06] border-amber-400/30"
                      : "bg-white/[0.03] border-white/10"
                }`}
              >
                <div className="flex items-center justify-between text-[10px] uppercase tracking-widest">
                  <span className={isOff ? "text-slate-500" : "text-slate-300"}>{d.label}</span>
                  {isOff && <span className="text-slate-500">Off</span>}
                  {isOvertime && <span className="text-amber-300">Overtime</span>}
                </div>
                <div className="flex items-center gap-2">
                  <input
                    type="number"
                    min="0"
                    max="24"
                    step="0.5"
                    value={values[d.key]}
                    onChange={(e) => setValues((prev) => ({ ...prev, [d.key]: e.target.value }))}
                    data-testid={`workday-hours-${d.key}`}
                    className="flex-1 bg-black/40 border border-white/10 rounded-xl px-3 py-2 text-lg text-white font-heading focus:outline-none focus:border-white/30"
                  />
                  <span className="text-xs text-slate-500 shrink-0">hrs</span>
                </div>
              </label>
            );
          })}
        </div>

        <div className="flex items-center justify-between flex-wrap gap-3 pt-2">
          <div className="text-xs text-slate-400" data-testid="workday-hours-total">
            Weekly capacity: <span className="text-white font-semibold">{total}h</span>
            {total === 0 && <span className="text-red-300 ml-2">— every day is Off. Set at least one workday to see workload data.</span>}
          </div>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={reset}
              data-testid="workday-hours-reset"
              className="px-4 py-2 rounded-full bg-white/5 border border-white/10 text-slate-200 text-sm hover:bg-white/10"
            >
              Reset to default
            </button>
            <button
              type="submit"
              disabled={saving}
              data-testid="workday-hours-save"
              className="px-5 py-2.5 rounded-full bg-white text-black text-sm font-semibold hover:bg-slate-100 disabled:opacity-60"
            >
              {saving ? "Saving…" : "Save hours"}
            </button>
          </div>
        </div>
      </form>
    </GlassPanel>
  );
}



/* ---------------------------------------------------------------------------- */
/* Enabled Services — admin toggle for which job types + installations show    */
/* in the pickers across Leads / Quotes / Jobs forms.                          */
/* ---------------------------------------------------------------------------- */
import { JOB_TYPES as ALL_JOB_TYPES, INSTALLATION_TYPES as ALL_INSTALLATION_TYPES } from "../lib/jobTypes";

function EnabledServicesSection() {
  const [jobTypes, setJobTypes] = useState(null);      // null = every type on
  const [instTypes, setInstTypes] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    let alive = true;
    api.get("/settings/enabled-services").then(({ data }) => {
      if (!alive) return;
      // Empty array on server = "no admin preference yet" — seed the
      // toggles with every option that isn't flagged `default_off` so
      // opt-in services (body kit installs, etc.) show as unticked
      // when the admin first opens this page.
      const seed = (all, saved) => new Set(
        (saved && saved.length) ? saved : all.filter((t) => !t.default_off).map((t) => t.value)
      );
      setJobTypes(seed(ALL_JOB_TYPES, data.job_types));
      setInstTypes(seed(ALL_INSTALLATION_TYPES, data.installation_types));
      setLoading(false);
    }).catch(() => {
      const seed = (all) => new Set(all.filter((t) => !t.default_off).map((t) => t.value));
      setJobTypes(seed(ALL_JOB_TYPES));
      setInstTypes(seed(ALL_INSTALLATION_TYPES));
      setLoading(false);
    });
    return () => { alive = false; };
  }, []);

  const toggle = (setter) => (value) => {
    setter((prev) => {
      const next = new Set(prev);
      if (next.has(value)) next.delete(value);
      else next.add(value);
      return next;
    });
    setDirty(true);
  };

  const toggleAll = (setter, allValues, on) => {
    setter(new Set(on ? allValues : []));
    setDirty(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/settings/enabled-services", {
        job_types: Array.from(jobTypes || []),
        installation_types: Array.from(instTypes || []),
      });
      // Refresh the window cache so pickers on other pages pick up the
      // change without needing a full reload.
      if (typeof window !== "undefined") {
        window.__enabledServices = {
          job_types: Array.from(jobTypes || []),
          installation_types: Array.from(instTypes || []),
        };
      }
      toast.success("Services updated");
      setDirty(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <GlassPanel className="!p-6"><div className="text-slate-400 text-sm">Loading…</div></GlassPanel>;
  }

  return (
    <GlassPanel data-testid="enabled-services-panel" className="!p-6 space-y-6">
      <div>
        <h2 className="font-heading text-xl text-white flex items-center gap-2">
          <Wrench className="w-5 h-5" /> Services offered
        </h2>
        <p className="text-xs text-slate-400 mt-1.5">
          Tick the services your shop actually offers. Anything unticked drops out of the pickers on Leads / Quotes / Jobs forms so your team sees a shorter list. Existing jobs keep their labels regardless.
        </p>
      </div>

      <ServiceToggleGroup
        title="Job types (what the customer is bringing in)"
        testIdPrefix="service-toggle-jobtype"
        selected={jobTypes}
        onToggle={toggle(setJobTypes)}
        onEnableAll={() => toggleAll(setJobTypes, ALL_JOB_TYPES.map((t) => t.value), true)}
        onDisableAll={() => toggleAll(setJobTypes, [], false)}
        options={ALL_JOB_TYPES.map((t) => ({ value: t.value, label: t.label }))}
      />

      <ServiceToggleGroup
        title="Installation types (what your shop does to it)"
        testIdPrefix="service-toggle-install"
        selected={instTypes}
        onToggle={toggle(setInstTypes)}
        onEnableAll={() => toggleAll(setInstTypes, ALL_INSTALLATION_TYPES.map((t) => t.value), true)}
        onDisableAll={() => toggleAll(setInstTypes, [], false)}
        options={ALL_INSTALLATION_TYPES.map((t) => ({ value: t.value, label: t.label }))}
      />

      <div className="flex items-center justify-end gap-3">
        {dirty && <span className="text-[11px] text-amber-300">Unsaved changes</span>}
        <button
          type="button"
          disabled={!dirty || saving}
          onClick={save}
          data-testid="enabled-services-save"
          className="bg-white text-black px-5 py-2 rounded-full text-sm font-semibold hover:bg-slate-100 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {saving ? "Saving…" : "Save changes"}
        </button>
      </div>
    </GlassPanel>
  );
}

function ServiceToggleGroup({ title, testIdPrefix, selected, options, onToggle, onEnableAll, onDisableAll }) {
  const enabledCount = options.filter((o) => selected.has(o.value)).length;
  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="text-sm text-white font-medium">{title}</div>
        <div className="flex items-center gap-2 text-[11px]">
          <span className="text-slate-400">{enabledCount} / {options.length} enabled</span>
          <button type="button" onClick={onEnableAll} className="px-2 py-1 rounded-full bg-white/5 hover:bg-white/10 text-slate-200 border border-white/10">Enable all</button>
          <button type="button" onClick={onDisableAll} className="px-2 py-1 rounded-full bg-white/5 hover:bg-white/10 text-slate-200 border border-white/10">Disable all</button>
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2">
        {options.map((opt) => {
          const on = selected.has(opt.value);
          return (
            <button
              type="button"
              key={opt.value}
              data-testid={`${testIdPrefix}-${opt.value}`}
              onClick={() => onToggle(opt.value)}
              className={`text-left px-3 py-2 rounded-xl border transition text-xs flex items-center justify-between gap-2 ${
                on
                  ? "bg-emerald-500/10 border-emerald-400/40 text-white"
                  : "bg-white/[0.02] border-white/10 text-slate-400 hover:text-white hover:border-white/25"
              }`}
            >
              <span className="truncate">{opt.label}</span>
              <span className={`w-4 h-4 rounded-full border flex items-center justify-center shrink-0 ${
                on ? "bg-emerald-400 border-emerald-300" : "bg-transparent border-white/25"
              }`}>
                {on && <span className="w-1.5 h-1.5 rounded-full bg-black" />}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

/* ---------------------------------------------------------------------------- */
/* JobWorkflowSection — admin toggles for the job pipeline enforcement rules   */
/* ---------------------------------------------------------------------------- */
function JobWorkflowSection() {
  const [gateOn, setGateOn] = useState(true);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let alive = true;
    api.get("/settings/job-workflow").then(({ data }) => {
      if (!alive) return;
      setGateOn(data.require_wrapping_gate !== false);
      setLoading(false);
    }).catch(() => setLoading(false));
    return () => { alive = false; };
  }, []);

  const toggle = async () => {
    const next = !gateOn;
    setGateOn(next);
    setSaving(true);
    try {
      await api.put("/settings/job-workflow", { require_wrapping_gate: next });
      if (typeof window !== "undefined") {
        window.__jobWorkflow = { require_wrapping_gate: next };
      }
      toast.success(next ? "Wrapping gate turned on" : "Wrapping gate turned off");
    } catch (err) {
      // Revert on failure so the UI stays honest.
      setGateOn(!next);
      toast.error(err.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <GlassPanel className="!p-6"><div className="text-slate-400 text-sm">Loading…</div></GlassPanel>;
  }

  return (
    <GlassPanel data-testid="job-workflow-panel" className="!p-6 space-y-4">
      <div>
        <h2 className="font-heading text-xl text-white flex items-center gap-2">
          <Wrench className="w-5 h-5" /> Job workflow
        </h2>
        <p className="text-xs text-slate-400 mt-1.5">
          Toggles for the way jobs move through the shop. Reverts (moving a job back to an earlier stage) always stay allowed regardless of these settings.
        </p>
      </div>

      <div
        className="flex items-start justify-between gap-4 rounded-2xl border border-white/10 bg-white/[0.03] px-4 py-4"
        data-testid="job-workflow-wrapping-gate-row"
      >
        <div className="flex-1 min-w-0">
          <div className="text-sm text-white font-medium">
            Require photo + hours + material before advancing past “Wrapping”
          </div>
          <p className="text-[11px] text-slate-400 mt-1 leading-relaxed">
            When on, the shop can't move a job past the Wrapping stage until the completion photo, actual hours, and estimated material used are filled in on the job card. Turn off if your crew fills these in later or your workflow doesn't need the checklist.
          </p>
        </div>
        <button
          type="button"
          role="switch"
          aria-checked={gateOn}
          disabled={saving}
          onClick={toggle}
          data-testid="job-workflow-wrapping-gate-toggle"
          className={`relative shrink-0 w-11 h-6 rounded-full transition-colors ${
            gateOn ? "bg-emerald-500" : "bg-slate-600/60"
          } ${saving ? "opacity-60" : ""}`}
        >
          <span className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform ${
            gateOn ? "translate-x-5" : "translate-x-0"
          }`} />
        </button>
      </div>
    </GlassPanel>
  );
}



/* ----------------------------------------------------------------------------
 * Custom Application Areas — per-vehicle custom entries that merge with the
 * built-in wrap / PPF area catalogue on the job form. Stops unique jobs
 * ("bull bar", "snorkel", "rear wing lip") from all landing in "Custom".
 * -------------------------------------------------------------------------- */
const JOB_TYPES_FOR_AREAS = [
  { key: "car",         label: "Car / SUV / Ute" },
  { key: "van",         label: "Van" },
  { key: "truck",       label: "Truck" },
  { key: "motorbike",   label: "Motorbike" },
  { key: "boat",        label: "Boat / marine" },
  { key: "commercial",  label: "Commercial fleet" },
  { key: "signage",     label: "Signage / building" },
  { key: "other",       label: "Other" },
];

function CustomAreasSection() {
  const [areas, setAreas] = React.useState({}); // { [jobType]: { wrap: [...], ppf: [...] } }
  const [jobType, setJobType] = React.useState("car");
  const [wrapDraft, setWrapDraft] = React.useState("");
  const [ppfDraft, setPpfDraft] = React.useState("");
  const [saving, setSaving] = React.useState(false);
  const [loaded, setLoaded] = React.useState(false);

  React.useEffect(() => {
    api.get("/settings/custom-areas")
      .then(({ data }) => setAreas(data || {}))
      .catch(() => setAreas({}))
      .finally(() => setLoaded(true));
  }, []);

  const bucket = areas[jobType] || { wrap: [], ppf: [] };

  const persist = async (next) => {
    setSaving(true);
    try {
      const { data } = await api.put("/settings/custom-areas", { areas: next });
      setAreas(data);
      refreshCustomAreas();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't save custom areas");
    } finally { setSaving(false); }
  };

  const addArea = async (kind, value) => {
    const clean = (value || "").trim();
    if (!clean) return;
    const cur = { wrap: [...(bucket.wrap || [])], ppf: [...(bucket.ppf || [])] };
    if (cur[kind].some((a) => a.toLowerCase() === clean.toLowerCase())) {
      toast.info("Already in the list");
      return;
    }
    cur[kind].push(clean);
    await persist({ ...areas, [jobType]: cur });
    if (kind === "wrap") setWrapDraft(""); else setPpfDraft("");
  };

  const removeArea = async (kind, area) => {
    const cur = { wrap: [...(bucket.wrap || [])], ppf: [...(bucket.ppf || [])] };
    cur[kind] = cur[kind].filter((a) => a !== area);
    await persist({ ...areas, [jobType]: cur });
  };

  if (!loaded) {
    return <GlassPanel className="!p-6"><div className="text-slate-400 text-sm">Loading custom areas…</div></GlassPanel>;
  }

  const renderBucket = (kind, draft, setDraft, label, helper) => (
    <div className="space-y-2" data-testid={`custom-areas-bucket-${kind}`}>
      <div className="flex items-center justify-between">
        <div>
          <div className="text-label">{label}</div>
          <div className="text-xs text-slate-500 mt-0.5">{helper}</div>
        </div>
        <span className="pill pill-muted text-[10px]">{(bucket[kind] || []).length}</span>
      </div>
      <div className="flex gap-2">
        <input
          type="text"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addArea(kind, draft); } }}
          placeholder={kind === "wrap" ? "e.g. Bull bar, Snorkel, Roof pod…" : "e.g. Roof rack scuff plate…"}
          data-testid={`custom-areas-input-${kind}`}
          className="flex-1 bg-white/[0.04] border border-white/10 rounded-xl px-3 py-2 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-emerald-400/40"
        />
        <button
          type="button"
          onClick={() => addArea(kind, draft)}
          disabled={saving || !draft.trim()}
          data-testid={`custom-areas-add-${kind}`}
          className="px-4 py-2 text-sm rounded-full bg-emerald-500 text-black font-semibold hover:bg-emerald-400 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Add
        </button>
      </div>
      <div className="flex flex-wrap gap-1.5 min-h-[24px]">
        {(bucket[kind] || []).length === 0 ? (
          <span className="text-xs text-slate-500 italic">No custom areas yet — the built-in list still applies.</span>
        ) : (
          (bucket[kind] || []).map((a) => (
            <span key={a} data-testid={`custom-areas-chip-${kind}-${a}`} className="pill pill-muted text-xs flex items-center gap-1">
              {a}
              <button
                type="button"
                onClick={() => removeArea(kind, a)}
                className="hover:text-red-300 transition"
                aria-label={`Remove ${a}`}
              ><X className="w-3 h-3" /></button>
            </span>
          ))
        )}
      </div>
    </div>
  );

  return (
    <GlassPanel className="!p-6 max-w-3xl space-y-6" data-testid="settings-custom-areas">
      <div>
        <div className="text-label flex items-center gap-2"><Sparkles className="w-3 h-3" /> Shop-specific</div>
        <h2 className="font-heading text-2xl text-white mt-1">Custom application areas</h2>
        <p className="text-slate-400 text-sm mt-1">
          Add the odd areas your shop handles that aren&apos;t in the default catalogue. They show up as tickable options in every job&apos;s installation form, so unique work gets categorised properly instead of piling up in &quot;Custom&quot;.
        </p>
      </div>

      <div>
        <div className="text-label mb-1.5">Vehicle type</div>
        <select
          value={jobType}
          onChange={(e) => setJobType(e.target.value)}
          data-testid="custom-areas-vehicle-select"
          className="w-full bg-white/[0.04] border border-white/10 rounded-xl px-3 py-2 text-sm text-white focus:outline-none focus:border-emerald-400/40"
        >
          {JOB_TYPES_FOR_AREAS.map((t) => (
            <option key={t.key} value={t.key} className="bg-neutral-900">{t.label}</option>
          ))}
        </select>
      </div>

      {renderBucket("wrap", wrapDraft, setWrapDraft, "Wrap areas", "Shown as ticks under Vinyl Wrap installations for this vehicle type.")}
      {renderBucket("ppf", ppfDraft, setPpfDraft, "PPF areas", "Shown as ticks under Paint Protection Film installations for this vehicle type.")}
    </GlassPanel>
  );
}


/* ----------------------------------------------------------------------------
 * Installation Terms & Conditions — customer-facing agreement sent with every
 * booking confirmation. Signed via /public/terms/:token. Also gates the
 * pipeline: jobs can't enter the workshop (`in_store`) until signed unless
 * an admin overrides.
 * -------------------------------------------------------------------------- */
function InstallationTermsSection() {
  const [form, setForm] = React.useState({ title: "Installation Terms & Conditions", body_markdown: "", enabled: true, min_job_price: 0 });
  const [loaded, setLoaded] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const [preview, setPreview] = React.useState(false);

  React.useEffect(() => {
    api.get("/settings/installation-terms")
      .then(({ data }) => setForm({
        title: data.title || "Installation Terms & Conditions",
        body_markdown: data.body_markdown || "",
        enabled: data.enabled !== false,
        min_job_price: Number(data.min_job_price) || 0,
      }))
      .catch(() => {})
      .finally(() => setLoaded(true));
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/settings/installation-terms", form);
      toast.success("Installation T&Cs saved — new bookings will link to this version");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't save terms");
    } finally { setSaving(false); }
  };

  const exportSignedTerms = async () => {
    try {
      const res = await api.get("/jobs/terms-pdf-export", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const stamp = new Date().toISOString().slice(0, 10);
      const a = document.createElement("a");
      a.href = url;
      a.download = `wrapconsole-signed-terms-${stamp}.zip`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
      toast.success("Export downloaded — check your Downloads folder");
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Couldn't build the export — no signed T&Cs on file yet");
    }
  };

  if (!loaded) {
    return <GlassPanel className="!p-6"><div className="text-slate-400 text-sm">Loading…</div></GlassPanel>;
  }

  return (
    <GlassPanel className="!p-6 max-w-3xl space-y-5" data-testid="settings-installation-terms">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-label flex items-center gap-2"><FileText className="w-3 h-3" /> Customer-facing</div>
          <h2 className="font-heading text-2xl text-white mt-1">Installation Terms &amp; Conditions</h2>
          <p className="text-slate-400 text-sm mt-1">
            Sent as a signing link in the booking confirmation email. Jobs cannot enter the workshop (<span className="text-slate-200">in_store</span> stage) until the customer signs — admins can override on a per-job basis.
          </p>
        </div>
        <label className="flex items-center gap-2 cursor-pointer select-none shrink-0">
          <span className="text-xs text-slate-400 uppercase tracking-widest">Enabled</span>
          <button
            type="button"
            onClick={() => setForm({ ...form, enabled: !form.enabled })}
            data-testid="installation-terms-toggle"
            className={`relative w-11 h-6 rounded-full transition-colors ${form.enabled ? "bg-emerald-500" : "bg-slate-600/60"}`}
          >
            <span className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform ${form.enabled ? "translate-x-5" : "translate-x-0"}`} />
          </button>
        </label>
      </div>

      <div>
        <label className="text-xs uppercase tracking-widest text-slate-400 block mb-1.5">Title</label>
        <input
          type="text"
          value={form.title}
          onChange={(e) => setForm({ ...form, title: e.target.value })}
          data-testid="installation-terms-title"
          className="w-full bg-white/[0.04] border border-white/10 rounded-xl px-4 py-2.5 text-white placeholder-slate-500 focus:outline-none focus:border-emerald-400/40"
        />
      </div>

      {/* Minimum job price for T&C requirement — lets shops skip the
          signature flow on small quick jobs (fuel-door respray, trim swap).
          0 = always require signing. Any value above 0 makes jobs with
          a quoted amount below that threshold bypass the T&C gate. */}
      <div>
        <label className="text-xs uppercase tracking-widest text-slate-400 block mb-1.5">
          Skip T&amp;C on jobs below this amount
        </label>
        <div className="relative max-w-[220px]">
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 text-sm">$</span>
          <input
            type="number"
            min="0"
            step="50"
            value={form.min_job_price}
            onChange={(e) => setForm({ ...form, min_job_price: e.target.value === "" ? 0 : Number(e.target.value) })}
            data-testid="installation-terms-min-job-price"
            placeholder="0"
            className="w-full bg-white/[0.04] border border-white/10 rounded-xl pl-7 pr-4 py-2.5 text-white placeholder-slate-500 focus:outline-none focus:border-emerald-400/40"
          />
        </div>
        <div className="text-[11px] text-slate-500 mt-1.5 leading-snug max-w-xl">
          {Number(form.min_job_price) > 0
            ? <>Jobs quoted below <span className="text-emerald-300 font-semibold">${Number(form.min_job_price).toLocaleString()}</span> will skip the T&amp;C sign-gate and move straight to the workshop. Larger jobs still require signing.</>
            : <>Every job requires a signed T&amp;C before entering the workshop. Set a dollar amount to exempt small quick jobs.</>}
        </div>
      </div>

      <div>
        <div className="flex items-center justify-between mb-1.5">
          <label className="text-xs uppercase tracking-widest text-slate-400">Body (markdown supported)</label>
          <button
            type="button"
            onClick={() => setPreview((p) => !p)}
            data-testid="installation-terms-preview-toggle"
            className="text-xs text-emerald-300 hover:text-emerald-200"
          >
            {preview ? "Edit" : "Preview"}
          </button>
        </div>
        {preview ? (
          <div className="bg-white/[0.03] border border-white/10 rounded-xl p-4 min-h-[200px] text-slate-200 text-sm whitespace-pre-wrap leading-relaxed" data-testid="installation-terms-preview">
            {form.body_markdown || <span className="text-slate-500 italic">Nothing to preview yet.</span>}
          </div>
        ) : (
          <textarea
            value={form.body_markdown}
            onChange={(e) => setForm({ ...form, body_markdown: e.target.value })}
            rows={16}
            data-testid="installation-terms-body"
            placeholder="## Payment
50% deposit required to secure your booking, remainder due on collection.

## Aftercare
Wait 48 hours before washing the vehicle. No commercial car washes for 30 days.

## Warranty
5-year manufacturer warranty on all films, 12-month labour warranty on workmanship."
            className="w-full bg-white/[0.04] border border-white/10 rounded-xl px-4 py-3 text-white placeholder-slate-500 focus:outline-none focus:border-emerald-400/40 font-mono text-[13px] leading-relaxed resize-y"
          />
        )}
        <div className="text-[11px] text-slate-500 mt-1.5">
          Use <code className="text-slate-300">##</code> for section headings and <code className="text-slate-300">-</code> for bullets. Blank lines break paragraphs.
        </div>
      </div>

      <div className="flex justify-end gap-2">
        <button
          type="button"
          onClick={exportSignedTerms}
          data-testid="installation-terms-export-zip"
          className="px-5 py-2.5 rounded-full bg-white/[0.04] border border-white/10 text-slate-200 text-sm font-medium hover:bg-white/[0.08] transition flex items-center gap-2"
          title="Download every signed T&Cs PDF as a single zip (with a summary CSV) for legal/audit hand-off"
        >
          <Download className="w-3.5 h-3.5" /> Export signed T&amp;Cs (.zip)
        </button>
        <button
          type="button"
          onClick={save}
          disabled={saving}
          data-testid="installation-terms-save"
          className="px-6 py-2.5 rounded-full bg-emerald-500 text-black text-sm font-semibold hover:bg-emerald-400 disabled:opacity-60"
        >
          {saving ? "Saving…" : "Save terms"}
        </button>
      </div>
    </GlassPanel>
  );
}
